import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import Messaging, { countUnreadConversations } from '../../components/Messaging';
import { useSupportInbox, HelpSidebarButton, HelpSettingsCard } from '../../components/SupportInbox';
import NotificationBell from '../../components/NotificationBell';
import LanguageToggle from '../../components/LanguageToggle';
import { useLanguagePreference } from '../../i18n/useLanguagePreference';
import { startConversationWithUnion } from '../../utils/startConversationWithUnion';
import { sendNotificationEmail } from '../../utils/sendNotificationEmail';
import DeleteAccountModal from '../../components/DeleteAccountModal';
import ImageCropModal from '../../components/ImageCropModal';
import ProfileCompletionBanner from '../../components/ProfileCompletionBanner';
import { renderJobDescription } from '../../utils/renderDescription';
import { getInterviewCountdown, URGENCY_COLORS } from '../../utils/interviewCountdown';
import StatCard from '../../components/StatCard';
import ActivityFeed from '../../components/ActivityFeed';
import { countThisWeek } from '../../utils/activityFeed';
import { normalizeExternalUrl } from '../../utils/examDates';
import LegalLinksRow from '../../components/LegalLinksRow';
import ZoomableImg from '../../components/ZoomableImg';
import UnionExamsPanel from '../../components/UnionExamsPanel';
import { joinParts } from '../../utils/joinParts';
import PortfolioManager from '../../components/PortfolioManager';
import { resolveUnionId, UNION_STAFF_ROLE } from '../../utils/resolveUnionId';
import { resolveEmployerId } from '../../utils/resolveEmployerId';
import VocationWorksLogo from '../../components/VocationWorksLogo';
import UnionApplicantCompare from '../../components/compare/UnionApplicantCompare';
import { CompareToggle, CompareEntryButton } from '../../components/compare/CompareOverlay';
import { useCompareSelection } from '../../utils/useCompareSelection';
import ApplicantReviewControls from '../../components/ApplicantReviewControls';
import { REVIEW_EMBED, reviewFromEmbed, requirementsTotal } from '../../utils/applicationReviews';
import LineIcon from '../../components/LineIcon';

const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const GREEN     = '#1D9E75';
const RED       = '#DC2626';
const GRAY      = '#6B7280';
const ATTENTION_RED      = '#E24B4A';
const ATTENTION_RED_TEXT = '#F09595';
const TREND_GREEN        = '#5DCAA5';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";
const NAV_H     = 56;
const SIDEBAR_W = 190;

const TRADES = [
  { value: 'Electrician',         key: 'electrician' },
  { value: 'Plumber',             key: 'plumber' },
  { value: 'Welder',              key: 'welder' },
  { value: 'HVAC Technician',     key: 'hvacTechnician' },
  { value: 'Carpenter',           key: 'carpenter' },
  { value: 'Pipefitter',          key: 'pipefitter' },
  { value: 'Ironworker',          key: 'ironworker' },
  { value: 'Sheet Metal Worker',  key: 'sheetMetalWorker' },
  { value: 'Millwright',          key: 'millwright' },
  { value: 'CNC Operator',        key: 'cncOperator' },
  { value: 'Industrial Maintenance', key: 'industrialMaintenance' },
  { value: 'Machine Operator',    key: 'machineOperator' },
  { value: 'General Labor',       key: 'generalLabor' },
  { value: 'Multiple Trades',     key: 'multipleTrades' },
];

const TABS = ['Dashboard', 'Apprenticeships', 'Exams', 'Candidates', 'Messages', 'Profile', 'Team', 'Settings'];

const TAB_LABEL_KEYS = {
  'Dashboard': 'nav.union.dashboard',
  'Apprenticeships': 'nav.union.apprenticeships',
  'Exams': 'nav.union.exams',
  'Candidates': 'nav.union.candidates',
  'Messages': 'nav.union.messages',
  'Profile': 'nav.union.profile',
  'Team': 'nav.union.team',
  'Settings': 'nav.union.settings',
};

const DURATION_OPTIONS = ['1 Year', '2 Years', '3 Years', '4 Years', '5 Years'];

const inp = {
  width: '100%', padding: '9px 12px', fontSize: '0.88rem',
  border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px',
  color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)',
  fontFamily: FONT_BODY,
};
const sel = { ...inp, cursor: 'pointer' };
const txt = { ...inp, resize: 'vertical', minHeight: '90px' };
const lbl = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '5px' };
const fld = { marginBottom: '1rem' };
const g2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' };

function IconHome({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}
function IconBriefcase({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  );
}
function IconUsers({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function IconMsg({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
function IconCalendar({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}
function IconClipboard({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <polyline points="9 14 11 16 15 12" />
    </svg>
  );
}
function IconBuilding({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="1" />
      <line x1="9" y1="7" x2="9" y2="7.01" />
      <line x1="15" y1="7" x2="15" y2="7.01" />
      <line x1="9" y1="12" x2="9" y2="12.01" />
      <line x1="15" y1="12" x2="15" y2="12.01" />
      <line x1="9" y1="17" x2="15" y2="17" />
    </svg>
  );
}
function IconUserPlus({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="20" y1="8" x2="20" y2="14" />
      <line x1="23" y1="11" x2="17" y2="11" />
    </svg>
  );
}
function IconSettings({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function UnionAvatar({ logoUrl, initials, size = 36, bg = NAVY_MID, textColor = WHITE }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.36, fontWeight: 700, color: textColor, overflow: 'hidden',
    }}>
      {logoUrl
        ? <ZoomableImg src={logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        : (initials || '?')}
    </div>
  );
}

function apprenticeshipStatusBadge(status, t) {
  if (status === 'closed') return { label: t('union.dashboard.apprModal.closedStatus'), color: GRAY };
  if (status === 'draft')  return { label: t('union.dashboard.apprModal.draftStatus'), color: AMBER };
  return { label: t('union.dashboard.shared.activeStatus'), color: GREEN };
}

function candidateStatusBadge(status, t) {
  if (status === 'reviewing') return { label: t('union.dashboard.candidates.statusReviewing'), color: AMBER };
  if (status === 'accepted')  return { label: t('union.dashboard.candidates.statusAccepted'), color: GREEN };
  if (status === 'rejected')  return { label: t('union.dashboard.candidates.statusRejected'), color: RED };
  return { label: t('union.dashboard.candidates.statusPending'), color: GRAY };
}

const EMPTY_APPR_FORM = {
  title: '', trade: '', description: '', requirements: '',
  start_date: '', duration: '', duration_preset: '', duration_custom: '',
  hourly_rate_start: '', hourly_rate_journeyman: '',
  city: 'Chicago', state: 'IL', gi_bill_eligible: false, drug_screening_required: false,
  resource_link_url: '', resource_link_label: '',
};

// Old free-text duration values ("3 years", etc.) won't exactly match a preset option --
// fall back to "Other" with the raw text preserved so editing never loses/corrupts data.
function durationToPresetFields(rawDuration) {
  const raw = (rawDuration || '').trim();
  const matched = DURATION_OPTIONS.find(opt => opt.toLowerCase() === raw.toLowerCase());
  if (matched) return { duration_preset: matched, duration_custom: '' };
  return { duration_preset: raw ? 'Other' : '', duration_custom: raw };
}

export default function UnionDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const tabLabel = (tab) => TAB_LABEL_KEYS[tab] ? t(TAB_LABEL_KEYS[tab]) : tab;
  const logoInputRef = useRef(null);

  // userId is the *union entity* id (union_accounts.id) every union-side query keys on --
  // the same role employer.id plays in EmployerDashboard. myUserId is the individual
  // logged-in person, which differs from userId for an invited union_staff seat.
  const [userId, setUserId]           = useState(null);
  const [myUserId, setMyUserId]       = useState(null);
  const [myStaffProfile, setMyStaffProfile] = useState(null); // { display_name, avatar_url, role }
  const [accessDeactivated, setAccessDeactivated] = useState(false);
  useLanguagePreference(userId);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [unionAccount, setUnionAccount] = useState(null);
  const [activeTab, setActiveTab]     = useState('Dashboard');
  const [refreshKey, setRefreshKey]   = useState(0);
  const [isMobile, setIsMobile]       = useState(window.innerWidth < 768);
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);
  const [upcomingInterviewsCount, setUpcomingInterviewsCount] = useState(0);
  const [interviewsScheduledThisWeek, setInterviewsScheduledThisWeek] = useState(0);
  const [now, setNow]                               = useState(() => new Date());
  const [linkCopied, setLinkCopied]   = useState(false);

  const [apprenticeships, setApprenticeships] = useState([]);
  const [apprenticeshipsLoaded, setApprenticeshipsLoaded] = useState(false);
  const [apprArchiveFilter, setApprArchiveFilter] = useState('active');
  const [apprSearchQuery, setApprSearchQuery]     = useState('');
  const [apprSortBy, setApprSortBy]               = useState('newest');
  const [expandedAppr, setExpandedAppr]           = useState(null);
  const [candidates, setCandidates]           = useState([]);
  const [candidatesArchiveFilter, setCandidatesArchiveFilter] = useState('active');
  // Applicants picked with ☆ for side-by-side comparison (apprenticeship_applications ids).
  const applicantCompare = useCompareSelection();
  const [showApplicantCompare, setShowApplicantCompare] = useState(false);
  const [highlightWorkerId, setHighlightWorkerId]         = useState(null);
  // Mobile Candidates cards start collapsed; ids of the ones opened to their full controls.
  const [expandedCandidateIds, setExpandedCandidateIds]   = useState(() => new Set());
  const [newCandidatesCount, setNewCandidatesCount] = useState(0);
  const [convUnreadCount, setUnreadCount]     = useState(0);

  // Vocation Works Support conversation (see useSupportInbox). An unread support thread counts as
  // one unread conversation in the Messages badge, the same unit as every other conversation.
  const support = useSupportInbox({ supabase, accountType: 'union', accountId: userId, viewerId: myUserId });
  const unreadCount = convUnreadCount + (support.hasUnread ? 1 : 0);
  const [messagesThisWeek, setMessagesThisWeek] = useState(0);
  const [openConversationId, setOpenConversationId] = useState(null);

  const [showApprModal, setShowApprModal] = useState(false);
  const [apprModalMode, setApprModalMode] = useState('create');
  const [editingApprId, setEditingApprId] = useState(null);
  const [apprForm, setApprForm]           = useState(EMPTY_APPR_FORM);
  const [savingAppr, setSavingAppr]       = useState(false);
  // Exam links to carry over when the modal was opened via Duplicate: [{ exam_id, name }]
  const [duplicateExamLinks, setDuplicateExamLinks] = useState([]);

  const [profileForm, setProfileForm]     = useState(null);
  const [profileMsg, setProfileMsg]       = useState('');
  const [profileMsgIsError, setProfileMsgIsError] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoMsg, setLogoMsg]             = useState('');
  const [logoMsgIsError, setLogoMsgIsError] = useState(false);
  const [cropFile, setCropFile]           = useState(null);
  const [dragActive, setDragActive]       = useState(false);

  const [pwForm, setPwForm] = useState({ newPw: '', confirmPw: '' });
  const [pwMsg, setPwMsg]   = useState('');
  const [pwMsgIsSuccess, setPwMsgIsSuccess] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);

  const [payScaleRows, setPayScaleRows]     = useState([]);
  // One flag for both editors -- they share a single load.
  const [profileRowsLoaded, setProfileRowsLoaded] = useState(false);
  const [savingPayScale, setSavingPayScale] = useState(false);
  const [payScaleMsg, setPayScaleMsg]       = useState('');

  // Team tab
  const [teamMembers, setTeamMembers]           = useState([]);
  const [staffInvites, setStaffInvites]         = useState([]);
  const [invitesLoaded, setInvitesLoaded]       = useState(false);
  const [inviteEmailInput, setInviteEmailInput] = useState('');
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const [inviteErrorMsg, setInviteErrorMsg]     = useState('');
  const [inviteEmailStatusMsg, setInviteEmailStatusMsg] = useState(null);
  const [lastInviteLink, setLastInviteLink]     = useState('');
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false);
  const [staffActiveSaving, setStaffActiveSaving] = useState({});
  const [staffActiveError, setStaffActiveError] = useState('');

  // My Profile (this individual staff member's own name/photo)
  const [profileNameForm, setProfileNameForm]   = useState('');
  const [profileSaving, setProfileSaving]       = useState(false);
  const [myProfileMsg, setMyProfileMsg]         = useState('');
  const [myProfileMsgIsError, setMyProfileMsgIsError] = useState(false);
  const [avatarCropFile, setAvatarCropFile]     = useState(null);
  const [avatarUploading, setAvatarUploading]   = useState(false);

  const [benefitRows, setBenefitRows]       = useState([]);
  const [savingBenefits, setSavingBenefits] = useState(false);
  const [benefitsMsg, setBenefitsMsg]       = useState('');

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);

    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      body { background: ${NAVY}; font-family: 'DM Sans', sans-serif; }
      input::placeholder, textarea::placeholder { color: rgba(255,255,255,0.28); }
      select option { background-color: ${NAVY_MID}; color: ${WHITE}; }
      .ud-nav-mobile-btn { display: none; }
      .ud-mobile-spacer  { display: none; }
      .ud-messages-wrap  { margin: 0 -24px -40px; }
      .ud-tab-scroll::-webkit-scrollbar { display: none; }
      @media (max-width: 767px) {
        .ud-tab-bar        { display: none !important; }
        .ud-bottom-nav     { display: flex !important; }
        .ud-sidebar        { display: none !important; }
        .ud-nav-mobile-btn { display: flex !important; }
        .ud-main           { margin-left: 0 !important; padding-bottom: calc(76px + env(safe-area-inset-bottom)) !important; padding-left: 16px !important; padding-right: 16px !important; }
        .ud-mobile-spacer  { display: block !important; height: calc(76px + env(safe-area-inset-bottom)) !important; }
        .ud-messages-wrap  { margin: 0 -16px 0 !important; }
        .ud-stats-grid     { grid-template-columns: 1fr 1fr !important; }
        .ud-two-col        { grid-template-columns: 1fr !important; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Tab row (768px and up): scrolls sideways with its scrollbar hidden. A fade on whichever
  // edge has more tabs signals there is more, and the active tab is kept in view.
  const tabRowRef = useRef(null);
  const [tabRowFade, setTabRowFade] = useState({ left: false, right: false });
  const updateTabRowFade = () => {
    const el = tabRowRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setTabRowFade(prev => (prev.left === left && prev.right === right ? prev : { left, right }));
  };
  useEffect(() => {
    window.addEventListener('resize', updateTabRowFade);
    document.fonts?.ready.then(updateTabRowFade);
    return () => window.removeEventListener('resize', updateTabRowFade);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = tabRowRef.current;
    const btn = el?.querySelector(`[data-tab="${activeTab}"]`);
    if (btn) {
      const pad = 64; // clear of the edge fade
      if (btn.offsetLeft < el.scrollLeft + pad) el.scrollTo({ left: Math.max(0, btn.offsetLeft - pad), behavior: 'smooth' });
      else if (btn.offsetLeft + btn.offsetWidth > el.scrollLeft + el.clientWidth - pad) el.scrollTo({ left: btn.offsetLeft + btn.offsetWidth - el.clientWidth + pad, behavior: 'smooth' });
    }
    updateTabRowFade();
  }, [activeTab, isMobile, !!unionAccount, t]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const uid = session?.user?.id;
      if (!uid) { navigate('/login'); return; }

      // "Which union does this login act as" isn't always "this login's own id" -- an invited
      // union_staff seat has its own auth.uid() but acts on the owner's union_id.
      const unionId = await resolveUnionId(supabase, uid);

      if (!unionId) {
        // An invited staff member the owner has deactivated: the DB already blocks every
        // union-side read/write for them -- say so plainly instead of bouncing them somewhere
        // unrelated. (Metadata is routing-only here; it grants nothing.)
        if (session.user.user_metadata?.role === UNION_STAFF_ROLE) {
          setAccessDeactivated(true);
          return;
        }
        // This session isn't a union account (e.g. an employer account whose session
        // ended up here) -- send them to the dashboard that actually matches their role
        // instead of rendering union-only chrome around empty/broken data.
        const employerId = await resolveEmployerId(supabase, uid);
        navigate(employerId ? '/employer/dashboard' : '/login');
        return;
      }

      const [{ data }, { data: myStaffRow }] = await Promise.all([
        supabase.from('union_accounts').select('*').eq('id', unionId).single(),
        supabase.from('union_staff').select('display_name, avatar_url, role').eq('union_id', unionId).eq('user_id', uid).maybeSingle(),
      ]);
      if (!data) { navigate('/login'); return; }

      setUserId(unionId);
      setMyUserId(uid);
      setMyStaffProfile(myStaffRow || null);
      setProfileNameForm(myStaffRow?.display_name || '');
      setUnionAccount(data);
      setEmailNotifications(data?.email_notifications !== false);
    };
    init();
  }, [navigate]);

  // ── Sidebar: up to 2 soonest upcoming interviews, read straight from the interviews table ──
  // Interviews live independently of conversation state, so hiding/deleting a conversation
  // no longer affects this. Depends on refreshKey so the realtime `interviews` subscription
  // below (new interview, or a status change like accept/decline) forces a recompute.
  useEffect(() => {
    if (!userId) return;
    const loadUpcomingInterviews = async () => {
      const { data: rows } = await supabase
        .from('interviews')
        .select('id, conversation_id, worker_id, interview_at, meeting_type, meeting_link, status, created_at')
        .eq('union_id', userId)
        .not('status', 'in', '(cancelled,completed)')
        .gte('interview_at', new Date().toISOString())
        .order('interview_at', { ascending: true });
      if (!rows?.length) { setUpcomingInterviews([]); setUpcomingInterviewsCount(0); setInterviewsScheduledThisWeek(0); return; }
      setUpcomingInterviewsCount(rows.length);
      setInterviewsScheduledThisWeek(countThisWeek(rows, 'created_at'));

      const mapped = await Promise.all(rows.slice(0, 2).map(async (upcoming) => {
        const { data: workerRow } = await supabase.from('workers').select('first_name, last_name').eq('id', upcoming.worker_id).single();
        const otherPartyName = workerRow ? `${workerRow.first_name || ''} ${workerRow.last_name || ''}`.trim() : '';
        return {
          id: upcoming.id,
          conversationId: upcoming.conversation_id,
          otherPartyName,
          interviewAt: upcoming.interview_at,
          meetingType: upcoming.meeting_type,
          meetingLink: upcoming.meeting_link,
          status: upcoming.status,
        };
      }));
      setUpcomingInterviews(mapped);
    };
    loadUpcomingInterviews();
  }, [userId, refreshKey]); // eslint-disable-line

  // ── Live countdown labels: re-render the upcoming-interview card(s) every 30s so the
  // "Interview in X min" / "Starting now" labels stay accurate without a page reload ──
  useEffect(() => {
    if (!upcomingInterviews.length) return;
    const tick = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(tick);
  }, [upcomingInterviews.length]);

  // ── Clicking the upcoming-interview card joins the meeting directly when a link
  // exists; otherwise falls back to opening the associated conversation ──────────
  const openUpcomingInterview = (interview) => {
    if (interview.meetingLink) {
      if (interview.meetingType === 'daily') {
        window.open(`/interview?interviewId=${interview.id}`, '_blank');
      } else {
        window.open(interview.meetingLink, '_blank', 'noopener,noreferrer');
      }
    } else {
      setActiveTab('Messages');
      setOpenConversationId(interview.conversationId);
    }
  };

  // ── Realtime: recompute the upcoming-interview card on a conversation hidden-status
  // change (affects unread count), and independently on any interview change ─────
  useEffect(() => {
    if (!userId) return;
    const convsChannel = supabase
      .channel(`conversations:union:${userId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'conversations', filter: `union_id=eq.${userId}` },
        () => setRefreshKey(k => k + 1)
      )
      .subscribe();
    const interviewsChannel = supabase
      .channel(`interviews:union:${userId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'interviews', filter: `union_id=eq.${userId}` },
        () => setRefreshKey(k => k + 1)
      )
      .subscribe();
    // New apprenticeship applications and status changes (accept/decline, archive, etc.)
    // need their own subscription to keep the Candidates list and badge live -- neither
    // is covered by the conversations/interviews channels above.
    const apprAppsChannel = supabase
      .channel(`apprenticeship_applications:union:${userId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'apprenticeship_applications', filter: `union_id=eq.${userId}` },
        () => loadCandidates(userId)
      )
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'apprenticeship_applications', filter: `union_id=eq.${userId}` },
        () => loadCandidates(userId)
      )
      .subscribe();
    return () => {
      supabase.removeChannel(convsChannel);
      supabase.removeChannel(interviewsChannel);
      supabase.removeChannel(apprAppsChannel);
    };
  }, [userId]); // eslint-disable-line

  useEffect(() => {
    if (unionAccount) {
      setProfileForm({
        union_name:   unionAccount.union_name || '',
        local_number: unionAccount.local_number || '',
        trade:        unionAccount.trade || '',
        phone:        unionAccount.phone || '',
        website:      unionAccount.website || '',
        address:      unionAccount.address || '',
        city:         unionAccount.city || 'Chicago',
        state:        unionAccount.state || 'IL',
        about:        unionAccount.about || '',
        how_to_apply: unionAccount.how_to_apply || '',
      });
    }
  }, [unionAccount]);

  const loadApprenticeships = async (uid) => {
    const { data } = await supabase
      .from('apprenticeships')
      .select('*')
      .eq('union_id', uid)
      .order('created_at', { ascending: false });
    setApprenticeships(data || []);
    setApprenticeshipsLoaded(true);
  };

  const loadCandidates = async (uid) => {
    // Explicit columns, never '*': the exam result / requirements met / staff notes columns that
    // used to sit on this table are being dropped, and those values now come from the staff-only
    // review table via the embed (null when nothing has been recorded).
    const { data: apps } = await supabase
      .from('apprenticeship_applications')
      .select(`id, apprenticeship_id, union_id, worker_id, status, created_at, updated_at, archived_at, ${REVIEW_EMBED}`)
      .eq('union_id', uid)
      .order('created_at', { ascending: false });

    if (!apps || apps.length === 0) { setCandidates([]); setNewCandidatesCount(0); return; }

    const workerIds = [...new Set(apps.map(a => a.worker_id))];
    const apprIds   = [...new Set(apps.map(a => a.apprenticeship_id))];

    const [{ data: workers }, { data: profiles }, { data: trades }, { data: apprs }] = await Promise.all([
      supabase.from('workers').select('id, first_name, last_name').in('id', workerIds),
      supabase.from('worker_profiles').select('id, city, state').in('id', workerIds),
      supabase.from('worker_trades').select('worker_id, trade').in('worker_id', workerIds),
      supabase.from('apprenticeships').select('id, title, requirements').in('id', apprIds),
    ]);

    const workerMap  = Object.fromEntries((workers || []).map(w => [w.id, w]));
    const profileMap = Object.fromEntries((profiles || []).map(p => [p.id, p]));
    const tradeMap    = Object.fromEntries((trades || []).map(t => [t.worker_id, t.trade]));
    const apprMap     = Object.fromEntries((apprs || []).map(a => [a.id, a]));

    setCandidates(apps.map(({ apprenticeship_application_reviews: reviewEmbed, ...a }) => ({
      ...a,
      review:        reviewFromEmbed(reviewEmbed),
      worker:        workerMap[a.worker_id],
      profile:       profileMap[a.worker_id],
      trade:         tradeMap[a.worker_id],
      apprenticeship: apprMap[a.apprenticeship_id],
    })));

    // Candidates badge: new pending applications, or any status change, since last visit.
    // Namespaced separately from the employer dashboard's 'lastCandidatesVisit' key so a
    // union account and an employer account in the same browser never cross-contaminate
    // each other's "seen" state.
    const lastVisit = localStorage.getItem('lastUnionCandidatesVisit') || '0';
    const newCount = apps.filter(a => {
      const isNewApplication = (!a.status || a.status === 'pending') && a.created_at > lastVisit;
      const hasStatusChange = a.updated_at && a.updated_at > lastVisit && a.updated_at !== a.created_at;
      return isNewApplication || hasStatusChange;
    }).length;
    setNewCandidatesCount(newCount);
  };

  // Which row the pay scale / benefits editor owns. A union linked to a legacy curated
  // directory entry (unions.union_account_id) edits THAT entry's rows in place: writing to its
  // own account id would create a second set the public page never shows, since that page
  // prefers account-keyed rows and only falls back to the curated ones when there are none.
  // Every union without a link -- the normal case going forward -- uses its own account id.
  // Resolved per call rather than held in state, so a Save can never act on a stale id.
  const resolvePayScaleOwnerId = async (uid) => {
    const { data } = await supabase.from('unions').select('id').eq('union_account_id', uid).maybeSingle();
    return data?.id || uid;
  };

  const loadPayScalesAndBenefits = async (uid) => {
    try {
      const ownerId = await resolvePayScaleOwnerId(uid);
      const [{ data: ps }, { data: bf }] = await Promise.all([
        supabase.from('union_pay_scales').select('*').eq('union_id', ownerId).order('sort_order'),
        supabase.from('union_benefits').select('*').eq('union_id', ownerId).order('sort_order'),
      ]);
      setPayScaleRows((ps || []).map(p => ({ id: p.id, level: p.level || '', hourly_rate: p.hourly_rate ?? '' })));
      setBenefitRows((bf || []).map(b => ({ id: b.id, benefit: b.benefit || '', description: b.description || '' })));
    } finally {
      // Both editors save by replacing every row they hold, so until this load lands they hold
      // an empty list that would wipe the union's real rows. Editing stays locked until then --
      // in a finally, so a failed load unlocks rather than trapping the editor forever.
      setProfileRowsLoaded(true);
    }
  };

  const loadUnreadCount = async (uid) => {
    // Excludes conversations this union has hidden -- those don't appear in the
    // Messages list, so unread messages inside them would otherwise be un-clearable.
    const { data: convs } = await supabase.from('conversations').select('id').eq('union_id', uid).is('union_hidden_at', null);
    if (!convs || convs.length === 0) { setUnreadCount(0); setMessagesThisWeek(0); return; }
    const ids = convs.map(c => c.id);
    // Badge unit: unread conversations, matching the Messages list (see countUnreadConversations).
    setUnreadCount(await countUnreadConversations(supabase, ids, 'union'));

    // Stat-card trend: messages actually received from a worker this week (distinct from
    // unreadCount above, which only tracks the currently-unread total, not a time window).
    const weekAgoIso = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const { count: weekCount } = await supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .in('conversation_id', ids)
      .eq('sender_type', 'worker')
      .gte('created_at', weekAgoIso);
    setMessagesThisWeek(weekCount || 0);
  };

  // ── Team tab: sent union_invites + the current union_staff roster (owner included) ──
  useEffect(() => {
    if (!userId) return;
    const loadTeam = async () => {
      const [{ data: invites }, { data: members }] = await Promise.all([
        supabase.from('union_invites').select('*').eq('union_id', userId).order('created_at', { ascending: false }),
        // is_active drives the Inactive treatment -- deactivated staff stay listed (greyed out,
        // reactivatable) rather than disappearing, since deactivation is reversible.
        supabase.from('union_staff').select('user_id, role, display_name, avatar_url, created_at, is_active').eq('union_id', userId).order('created_at', { ascending: true }),
      ]);
      setStaffInvites(invites || []);
      setInvitesLoaded(true);
      setTeamMembers(members || []);
    };
    loadTeam();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    loadApprenticeships(userId);
    loadCandidates(userId);
    loadUnreadCount(userId);
    loadPayScalesAndBenefits(userId);
  }, [userId, refreshKey]);

  const addPayScaleRow    = () => setPayScaleRows(prev => [...prev, { level: '', hourly_rate: '' }]);
  const updatePayScaleRow = (idx, field, value) => setPayScaleRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const deletePayScaleRow = (idx) => setPayScaleRows(prev => prev.filter((_, i) => i !== idx));

  const handleSavePayScale = async () => {
    setSavingPayScale(true);
    setPayScaleMsg('');
    const ownerId = await resolvePayScaleOwnerId(userId);
    await supabase.from('union_pay_scales').delete().eq('union_id', ownerId);
    const rows = payScaleRows
      .filter(r => r.level)
      .map((r, i) => ({ union_id: ownerId, level: r.level, hourly_rate: r.hourly_rate ? Number(r.hourly_rate) : null, sort_order: i }));
    if (rows.length > 0) await supabase.from('union_pay_scales').insert(rows);
    await loadPayScalesAndBenefits(userId);
    setSavingPayScale(false);
    setPayScaleMsg(t('union.dashboard.profile.payScaleSaved'));
    setTimeout(() => setPayScaleMsg(''), 3000);
  };

  const addBenefitRow    = () => setBenefitRows(prev => [...prev, { benefit: '', description: '' }]);
  const updateBenefitRow = (idx, field, value) => setBenefitRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const deleteBenefitRow = (idx) => setBenefitRows(prev => prev.filter((_, i) => i !== idx));

  const handleSaveBenefits = async () => {
    setSavingBenefits(true);
    setBenefitsMsg('');
    const ownerId = await resolvePayScaleOwnerId(userId);
    await supabase.from('union_benefits').delete().eq('union_id', ownerId);
    const rows = benefitRows
      .filter(r => r.benefit)
      .map((r, i) => ({ union_id: ownerId, benefit: r.benefit, description: r.description || '', sort_order: i }));
    if (rows.length > 0) await supabase.from('union_benefits').insert(rows);
    await loadPayScalesAndBenefits(userId);
    setSavingBenefits(false);
    setBenefitsMsg(t('union.dashboard.profile.benefitsSaved'));
    setTimeout(() => setBenefitsMsg(''), 3000);
  };

  // Mark candidates as seen on visiting the tab, mirroring the employer dashboard's pattern.
  useEffect(() => {
    if (activeTab === 'Candidates') {
      localStorage.setItem('lastUnionCandidatesVisit', new Date().toISOString());
      setNewCandidatesCount(0);
    }
  }, [activeTab]);

  // Deep-link support (e.g. returning from a candidate's profile with a conversation
  // just started) -- mirrors the employer dashboard's location.state handling.
  useEffect(() => {
    if (location.state?.activeTab) setActiveTab(location.state.activeTab);
    else if (location.state?.tab) setActiveTab(location.state.tab);
    if (location.state?.conversationId) setOpenConversationId(location.state.conversationId);
  }, [location.state]);

  // Scroll to and briefly highlight the candidate a notification deep-linked to.
  useEffect(() => {
    if (!highlightWorkerId || activeTab !== 'Candidates') return;
    const target = candidates.find(c => c.worker_id === highlightWorkerId);
    if (!target) { setHighlightWorkerId(null); return; }

    const wantsArchived = !!target.archived_at;
    if (wantsArchived && candidatesArchiveFilter !== 'archived') { setCandidatesArchiveFilter('archived'); return; }
    if (!wantsArchived && candidatesArchiveFilter === 'archived') { setCandidatesArchiveFilter('active'); return; }

    const id = highlightWorkerId;
    setExpandedCandidateIds(prev => new Set([...prev, ...candidates.filter(c => c.worker_id === id).map(c => c.id)]));
    requestAnimationFrame(() => {
      document.querySelector(`[data-worker-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const t = setTimeout(() => setHighlightWorkerId(null), 3000);
    return () => clearTimeout(t);
  }, [highlightWorkerId, activeTab, candidates, candidatesArchiveFilter]); // eslint-disable-line

  const openPostModal = () => {
    setApprModalMode('create');
    setEditingApprId(null);
    setDuplicateExamLinks([]);
    setApprForm({ ...EMPTY_APPR_FORM, trade: unionAccount?.trade || '' });
    setShowApprModal(true);
  };

  const handleCopyProfileLink = () => {
    if (!unionAccount?.slug) return;
    navigator.clipboard.writeText(`${window.location.origin}/unions/${unionAccount.slug}`);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const openEditModal = (appr) => {
    setApprModalMode('edit');
    setEditingApprId(appr.id);
    setDuplicateExamLinks([]);
    setApprForm({
      title: appr.title || '',
      trade: appr.trade || '',
      description: appr.description || '',
      requirements: (appr.requirements || []).join('\n'),
      start_date: appr.start_date || '',
      duration: appr.duration || '',
      ...durationToPresetFields(appr.duration),
      hourly_rate_start: appr.hourly_rate_start ?? '',
      hourly_rate_journeyman: appr.hourly_rate_end ?? '',
      resource_link_url: appr.resource_link_url || '',
      resource_link_label: appr.resource_link_label || '',
      city: appr.city || 'Chicago',
      state: appr.state || 'IL',
      gi_bill_eligible: appr.gi_bill_eligible || false,
      drug_screening_required: appr.drug_screening_required || false,
    });
    setShowApprModal(true);
  };

  // Opens the same modal in "create" mode, pre-filled from an existing posting, so the
  // union can quickly post a similar program without retyping everything. Requirements ride
  // along in the form; linked exams live in apprenticeship_exam_requirements, so they're
  // fetched here and re-linked to the new posting after it's inserted.
  const openDuplicateModal = async (appr) => {
    setApprModalMode('create');
    setEditingApprId(null);
    // Awaited before the modal opens so Save can never run with the links still in flight.
    const { data: sourceLinks } = await supabase
      .from('apprenticeship_exam_requirements')
      .select('exam_id, union_exams(name)')
      .eq('apprenticeship_id', appr.id);
    setDuplicateExamLinks((sourceLinks || []).map(l => ({ exam_id: l.exam_id, name: l.union_exams?.name || '' })));
    setApprForm({
      title: appr.title || '',
      trade: appr.trade || '',
      description: appr.description || '',
      requirements: (appr.requirements || []).join('\n'),
      start_date: '',
      duration: appr.duration || '',
      ...durationToPresetFields(appr.duration),
      hourly_rate_start: appr.hourly_rate_start ?? '',
      hourly_rate_journeyman: appr.hourly_rate_end ?? '',
      resource_link_url: appr.resource_link_url || '',
      resource_link_label: appr.resource_link_label || '',
      city: appr.city || 'Chicago',
      state: appr.state || 'IL',
      gi_bill_eligible: appr.gi_bill_eligible || false,
      drug_screening_required: appr.drug_screening_required || false,
    });
    setShowApprModal(true);
  };

  const handleSaveApprenticeship = async () => {
    if (!apprForm.title || !apprForm.trade) {
      alert(t('union.dashboard.apprModal.titleTradeRequired'));
      return;
    }
    const resourceUrl = normalizeExternalUrl(apprForm.resource_link_url);
    if (!resourceUrl && apprForm.resource_link_label.trim()) {
      alert(t('union.dashboard.apprModal.resourceLinkNeedsUrl'));
      return;
    }
    setSavingAppr(true);
    const requirementsArray = apprForm.requirements
      .split('\n')
      .map(r => r.trim())
      .filter(r => r.length > 0);
    const payload = {
      title: apprForm.title,
      trade: apprForm.trade,
      description: apprForm.description,
      requirements: requirementsArray,
      start_date: apprForm.start_date,
      duration: apprForm.duration,
      hourly_rate_start: apprForm.hourly_rate_start ? Number(apprForm.hourly_rate_start) : null,
      hourly_rate_end: apprForm.hourly_rate_journeyman ? Number(apprForm.hourly_rate_journeyman) : null,
      city: apprForm.city,
      state: apprForm.state,
      gi_bill_eligible: apprForm.gi_bill_eligible,
      drug_screening_required: apprForm.drug_screening_required,
      resource_link_url: resourceUrl || null,
      // A label with no address has nothing to point at, so it isn't stored on its own.
      resource_link_label: resourceUrl ? (apprForm.resource_link_label.trim() || null) : null,
    };

    if (apprModalMode === 'create') {
      const { data: created } = await supabase.from('apprenticeships').insert({
        ...payload,
        union_id: userId,
        union_name: unionAccount?.union_name || '',
        status: 'active',
      }).select('id').single();
      if (created && duplicateExamLinks.length > 0) {
        const { error: linkError } = await supabase
          .from('apprenticeship_exam_requirements')
          .insert(duplicateExamLinks.map(l => ({ apprenticeship_id: created.id, exam_id: l.exam_id })));
        if (linkError) alert(t('union.dashboard.apprModal.duplicateExamsFailed'));
      }
    } else {
      await supabase.from('apprenticeships').update(payload).eq('id', editingApprId);
    }

    setSavingAppr(false);
    setShowApprModal(false);
    setRefreshKey(k => k + 1);
  };

  const handleCloseApprenticeship = async (appr) => {
    if (!window.confirm(t('union.dashboard.apprModal.closeConfirm', { title: appr.title }))) return;
    const { error } = await supabase.from('apprenticeships').update({ status: 'closed' }).eq('id', appr.id);
    if (error) {
      alert(t('union.dashboard.apprModal.closeFailed'));
      return;
    }
    setRefreshKey(k => k + 1);
  };

  // Mirrors the employer dashboard's status_change notification. The labels are union-specific
  // rather than copied: the employer's catch-all would tell an accepted applicant they were
  // "not selected". They match the badges the worker sees on their Apprenticeships tab, which
  // is also where the notification links, since that's where apprenticeship statuses live.
  const APPR_STATUS_LABELS = {
    pending:   'pending review',
    reviewing: 'under review',
    accepted:  'accepted',
    rejected:  'not selected',
  };

  // A review field ApplicantReviewControls has confirmed as persisted. Only confirmed values land
  // in `candidates`, so the summary chips and the Compare view never show an unsaved edit.
  const handleReviewPersisted = (applicationId, field, value) => {
    setCandidates(prev => prev.map(c => c.id === applicationId ? { ...c, review: { ...c.review, [field]: value } } : c));
  };

  const handleUpdateCandidateStatus = async (candidate, status) => {
    const { error } = await supabase.from('apprenticeship_applications').update({ status }).eq('id', candidate.id);
    if (error) return;
    setCandidates(prev => prev.map(c => c.id === candidate.id ? { ...c, status } : c));
    const statusLabel = APPR_STATUS_LABELS[status] || status;
    supabase.from('notifications').insert({
      user_id:   candidate.worker_id,
      user_type: 'worker',
      type:      'status_change',
      message:   `Your application for ${candidate.apprenticeship?.title} is now ${statusLabel}`,
      link_to:   JSON.stringify({ tab: 'Apprenticeships', apprenticeship_id: candidate.apprenticeship_id }),
    }).then(() => {});
    sendNotificationEmail(supabase, {
      recipientId:   candidate.worker_id,
      recipientType: 'worker',
      type:          'status_change',
      job_title:     candidate.apprenticeship?.title || '',
      status_label:  statusLabel,
    });
  };

  const handleArchiveApprenticeship = async (appr) => {
    if (!window.confirm(t('union.dashboard.apprModal.archiveConfirm', { title: appr.title }))) return;
    const archivedAt = new Date().toISOString();
    await supabase.from('apprenticeships').update({ archived_at: archivedAt }).eq('id', appr.id);
    await supabase.from('apprenticeship_applications').update({ archived_at: archivedAt }).eq('apprenticeship_id', appr.id);
    setRefreshKey(k => k + 1);
  };

  const handleUnarchiveApprenticeship = async (appr) => {
    await supabase.from('apprenticeships').update({ archived_at: null }).eq('id', appr.id);
    await supabase.from('apprenticeship_applications').update({ archived_at: null }).eq('apprenticeship_id', appr.id);
    setRefreshKey(k => k + 1);
  };

  const handleArchiveCandidate = async (candidateId) => {
    await supabase.from('apprenticeship_applications').update({ archived_at: new Date().toISOString() }).eq('id', candidateId);
    setRefreshKey(k => k + 1);
  };

  const handleUnarchiveCandidate = async (candidateId) => {
    await supabase.from('apprenticeship_applications').update({ archived_at: null }).eq('id', candidateId);
    setRefreshKey(k => k + 1);
  };

  const handleMessageCandidate = async (candidate) => {
    const convId = await startConversationWithUnion(supabase, candidate.worker_id, userId, candidate.apprenticeship_id, 'union');
    setOpenConversationId(convId);
    setActiveTab('Messages');
  };

  const handleLogoFile = (file) => {
    if (!file || !userId) return;
    setCropFile(file);
  };

  const handleLogoCropConfirm = async (blob, outputType) => {
    setCropFile(null);
    setLogoUploading(true);
    setLogoMsg('');
    const path = `${userId}/logo`;
    const { error: uploadError } = await supabase.storage
      .from('company-logos')
      .upload(path, blob, { upsert: true, contentType: outputType });
    if (uploadError) {
      setLogoMsg(t('union.dashboard.profile.logoUploadFailed'));
      setLogoMsgIsError(true);
      setLogoUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from('company-logos').getPublicUrl(path);
    const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
    await supabase.from('union_accounts').update({ logo_url: publicUrl }).eq('id', userId);
    setUnionAccount(prev => ({ ...prev, logo_url: publicUrl }));
    setLogoMsg(t('union.dashboard.profile.logoSaved'));
    setLogoMsgIsError(false);
    setLogoUploading(false);
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setProfileMsg('');
    const slug = (profileForm.union_name + ' ' + profileForm.local_number)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    const payload = { ...profileForm, slug };
    const { error } = await supabase.from('union_accounts').update(payload).eq('id', userId);
    setSavingProfile(false);
    if (error) {
      setProfileMsg(error.code === '23505' ? t('union.dashboard.profile.saveFailedNameTaken') : t('union.dashboard.profile.saveFailedGeneric'));
      setProfileMsgIsError(true);
      return;
    }
    setUnionAccount(prev => ({ ...prev, ...payload }));
    setProfileMsg(t('union.dashboard.profile.profileSaved'));
    setProfileMsgIsError(false);
    setTimeout(() => setProfileMsg(''), 3000);
  };

  const handleChangePassword = async () => {
    if (pwForm.newPw.length < 8) { setPwMsg(t('employer.dashboard.settings.passwordTooShort')); setPwMsgIsSuccess(false); return; }
    if (pwForm.newPw !== pwForm.confirmPw) { setPwMsg(t('employer.dashboard.settings.passwordMismatch')); setPwMsgIsSuccess(false); return; }
    const { error } = await supabase.auth.updateUser({ password: pwForm.newPw });
    if (error) { setPwMsg(error.message); setPwMsgIsSuccess(false); return; }
    setPwMsg(t('employer.dashboard.settings.passwordUpdated'));
    setPwMsgIsSuccess(true);
    setPwForm({ newPw: '', confirmPw: '' });
    setTimeout(() => setPwMsg(''), 3000);
  };

  const handleToggleEmailNotifications = async () => {
    const next = !emailNotifications;
    setEmailNotifications(next);
    await supabase.from('union_accounts').update({ email_notifications: next }).eq('id', userId);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // ── My Profile: this individual's own display name + avatar (union_staff row), distinct
  // from the union's shared name/logo. Mirrors EmployerDashboard's handlers exactly. ──
  const handleSaveProfileName = async () => {
    if (!myUserId || !userId) return;
    setProfileSaving(true);
    setMyProfileMsg('');
    const trimmed = profileNameForm.trim();
    const { error } = await supabase
      .from('union_staff')
      .update({ display_name: trimmed || null })
      .eq('union_id', userId)
      .eq('user_id', myUserId);
    setProfileSaving(false);
    if (error) {
      setMyProfileMsg(t('union.dashboard.settings.profileNameSaveFailed'));
      setMyProfileMsgIsError(true);
      return;
    }
    setMyStaffProfile(prev => ({ ...(prev || {}), display_name: trimmed || null }));
    setTeamMembers(prev => prev.map(m => m.user_id === myUserId ? { ...m, display_name: trimmed || null } : m));
    setMyProfileMsg(t('union.dashboard.settings.profileNameSaved'));
    setMyProfileMsgIsError(false);
    setTimeout(() => setMyProfileMsg(''), 2500);
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAvatarCropFile(file);
  };

  const handleAvatarCropConfirm = async (blob, outputType) => {
    setAvatarCropFile(null);
    if (!myUserId || !userId) return;
    setAvatarUploading(true);
    setMyProfileMsg('');
    // Shared staff-avatars bucket -- its storage policies key on the uploader's own uid folder.
    const path = `${myUserId}/avatar`;
    const { error: uploadError } = await supabase.storage
      .from('staff-avatars')
      .upload(path, blob, { upsert: true, contentType: outputType });
    if (uploadError) {
      setMyProfileMsg(t('union.dashboard.settings.avatarUploadFailed'));
      setMyProfileMsgIsError(true);
      setAvatarUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from('staff-avatars').getPublicUrl(path);
    const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
    const { error: rowError } = await supabase.from('union_staff').update({ avatar_url: publicUrl }).eq('union_id', userId).eq('user_id', myUserId);
    setAvatarUploading(false);
    if (rowError) {
      setMyProfileMsg(t('union.dashboard.settings.avatarUploadFailed'));
      setMyProfileMsgIsError(true);
      return;
    }
    setMyStaffProfile(prev => ({ ...(prev || {}), avatar_url: publicUrl }));
    setTeamMembers(prev => prev.map(m => m.user_id === myUserId ? { ...m, avatar_url: publicUrl } : m));
    setMyProfileMsg(t('union.dashboard.settings.avatarSaved'));
    setMyProfileMsgIsError(false);
    setTimeout(() => setMyProfileMsg(''), 2500);
  };

  // ── Team: soft deactivation, mirroring EmployerDashboard.handleToggleStaffActive. The real
  // enforcement is guard_union_staff_deactivation (owner-only, never your own row); this keeps
  // the UI honest and surfaces the trigger's message if it ever rejects. ──
  const handleToggleStaffActive = async (member) => {
    if (!userId) return;
    const deactivating = member.is_active !== false;
    const who = member.display_name || t('union.dashboard.team.unnamedMember');
    const confirmed = window.confirm(t(
      deactivating ? 'union.dashboard.team.deactivateConfirm' : 'union.dashboard.team.reactivateConfirm',
      { name: who },
    ));
    if (!confirmed) return;

    setStaffActiveSaving(prev => ({ ...prev, [member.user_id]: true }));
    setStaffActiveError('');
    const { data: updatedRows, error } = await supabase
      .from('union_staff')
      .update({ is_active: !deactivating })
      .eq('union_id', userId)
      .eq('user_id', member.user_id)
      .select('user_id');
    setStaffActiveSaving(prev => ({ ...prev, [member.user_id]: false }));

    if (error) { setStaffActiveError(error.message); return; }
    // RLS filters rather than errors on a non-matching UPDATE -- zero rows means nothing changed.
    if (!updatedRows?.length) { setStaffActiveError(t('union.dashboard.profile.saveFailedGeneric')); return; }
    setTeamMembers(prev => prev.map(m => m.user_id === member.user_id ? { ...m, is_active: !deactivating } : m));
  };

  const handleGenerateInvite = async () => {
    if (!userId || !myUserId) return;
    const trimmedEmail = inviteEmailInput.trim();
    setGeneratingInvite(true);
    setInviteErrorMsg('');
    setInviteLinkCopied(false);
    setInviteEmailStatusMsg(null);
    // union_invites.invite_code has no column default (unlike employer_invites), so the code is
    // generated here: 32 hex chars from the browser CSPRNG, same shape as encode(gen_random_bytes(16),'hex').
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    const inviteCode = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    const { data: newInvite, error } = await supabase
      .from('union_invites')
      .insert({ union_id: userId, created_by: myUserId, email: trimmedEmail || null, invite_code: inviteCode })
      .select('*')
      .single();
    if (error) { setGeneratingInvite(false); setInviteErrorMsg(error.message); return; }
    setStaffInvites(prev => [newInvite, ...prev]);
    const link = `${window.location.origin}/union/join?code=${newInvite.invite_code}`;
    setLastInviteLink(link);

    // Best-effort email on top of the DB row -- the link already works via copy regardless.
    if (trimmedEmail) {
      try {
        const res = await fetch('/api/notify-interest', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'union_staff_invite',
            recipient_email: trimmedEmail,
            union_name: unionAccount?.union_name || 'your union',
            invite_link: link,
          }),
        });
        setInviteEmailStatusMsg(res.ok
          ? { text: t('union.dashboard.team.inviteEmailSent', { email: trimmedEmail }), isError: false }
          : { text: t('union.dashboard.team.inviteEmailFailed'), isError: true });
      } catch (_) {
        setInviteEmailStatusMsg({ text: t('union.dashboard.team.inviteEmailFailed'), isError: true });
      }
      setInviteEmailInput('');
    }
    setGeneratingInvite(false);
  };

  const handleCopyInviteLink = () => {
    navigator.clipboard.writeText(lastInviteLink).then(() => {
      setInviteLinkCopied(true);
      setTimeout(() => setInviteLinkCopied(false), 2000);
    });
  };

  if (accessDeactivated) {
    return (
      <div style={{ background: NAVY, minHeight: '100dvh', fontFamily: FONT_BODY, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div data-access-deactivated style={{ background: NAVY_MID, borderTop: `3px solid ${AMBER}`, borderRadius: '12px', padding: '28px 24px', maxWidth: '440px', width: '100%', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '14px' }}><VocationWorksLogo variant="reversed" layout="mark" size={40} /></div>
          <h1 style={{ fontFamily: FONT_HEAD, fontSize: '1.9rem', color: WHITE, letterSpacing: '0.04em', marginBottom: '10px' }}>
            {t('union.dashboard.accessDeactivated.heading')}
          </h1>
          <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, marginBottom: '20px' }}>
            {t('union.dashboard.accessDeactivated.body')}
          </p>
          <button
            onClick={handleSignOut}
            style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.9rem', padding: '10px 22px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY }}
          >
            {t('common.signOut')}
          </button>
        </div>
      </div>
    );
  }

  if (!unionAccount) {
    return <div style={{ background: NAVY, minHeight: '100dvh' }} />;
  }

  // Owner-only capabilities are enforced in the DB (union_accounts UPDATE is id = auth.uid(),
  // union_invites INSERT is is_union_owner(), delete_own_union_account requires the owner);
  // the UI gates on the same role so staff never see an action that would just fail.
  const isOwner = myStaffProfile?.role === 'owner';
  const myDisplayName = myStaffProfile?.display_name || '';

  const unionInitials = (unionAccount.union_name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase() || '?';

  const unionCompletionItems = [
    { done: !!unionAccount.logo_url,     label: t('union.signup.step2.logoLabel'),        tab: 'Profile' },
    { done: !!unionAccount.about,        label: t('union.dashboard.profile.aboutLabel'),  tab: 'Profile' },
    { done: !!unionAccount.website,      label: t('union.dashboard.profile.websiteLabel'), tab: 'Profile' },
    { done: !!unionAccount.how_to_apply, label: t('union.dashboard.profile.howToApplyLabel'), tab: 'Profile' },
  ];
  const unionCompletedCount = unionCompletionItems.filter(i => i.done).length;
  const unionProfilePct     = Math.round((unionCompletedCount / unionCompletionItems.length) * 100 / 25) * 25;
  const unionMissingItems   = unionCompletionItems.filter(i => !i.done);

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: NAVY, fontFamily: FONT_BODY }}>

      {/* ── Navbar ── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, height: NAV_H,
        backgroundColor: NAVY, borderBottom: `2px solid ${AMBER}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 20px', zIndex: 100,
      }}>
        <div
          onClick={() => { setActiveTab('Dashboard'); setRefreshKey(k => k + 1); }}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
        >
          <VocationWorksLogo variant="reversed" size={34} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <LanguageToggle userId={userId} />
          <NotificationBell userId={userId} onNavigate={(tab, link) => {
            setActiveTab(tab);
            if (link?.conversation_id) setOpenConversationId(link.conversation_id);
            if (link?.worker_id) setHighlightWorkerId(link.worker_id);
          }} />
          <div className="ud-nav-mobile-btn" onClick={() => setActiveTab('Settings')} style={{ cursor: 'pointer', color: WHITE }}>
            <IconSettings color={WHITE} />
          </div>
          <div onClick={() => setActiveTab('Profile')} style={{ cursor: 'pointer' }}>
            <UnionAvatar logoUrl={unionAccount.logo_url} initials={unionInitials} size={30} bg={AMBER} textColor={NAVY} />
          </div>
        </div>
      </nav>

      {/* ── Left Sidebar ── */}
      <aside className="ud-sidebar" style={{
        position: 'fixed', top: NAV_H, left: 0, bottom: 0, width: SIDEBAR_W,
        backgroundColor: NAVY_MID, zIndex: 99,
        display: 'flex', flexDirection: 'column', overflowY: 'auto',
      }}>
        <div style={{ padding: '20px 14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <UnionAvatar logoUrl={unionAccount.logo_url} initials={unionInitials} size={60} bg={AMBER} textColor={NAVY} />
          </div>
          <p style={{ marginTop: '10px', fontWeight: 700, fontSize: '0.88rem', color: WHITE, lineHeight: 1.3 }}>
            {unionAccount.union_name}
          </p>
          {unionAccount.local_number && (
            <p style={{ fontSize: '0.8rem', color: AMBER, marginTop: '3px' }}>
              {unionAccount.local_number}
            </p>
          )}
          {(unionAccount.city || unionAccount.state) && (
            <p style={{ fontSize: '0.77rem', color: 'rgba(255,255,255,0.42)', marginTop: '4px' }}>
              {[unionAccount.city, unionAccount.state].filter(Boolean).join(', ')}
            </p>
          )}
          <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center' }}>
            <span style={{
              backgroundColor: unionAccount.deactivated_at ? 'rgba(220,38,38,0.15)' : 'rgba(29,158,117,0.15)',
              border: `1px solid ${unionAccount.deactivated_at ? RED : GREEN}`,
              borderRadius: '999px', padding: '3px 12px',
              fontSize: '0.7rem', fontWeight: 700, color: unionAccount.deactivated_at ? RED : GREEN,
            }}>
              {unionAccount.deactivated_at ? t('union.dashboard.shared.inactiveStatus') : t('union.dashboard.shared.activeStatus')}
            </span>
          </div>
          <div style={{ marginTop: '14px' }}>
            <p style={{ fontSize: '0.7rem', fontWeight: 600, color: unionProfilePct === 100 ? AMBER : 'rgba(255,255,255,0.4)', marginBottom: '6px' }}>
              {unionProfilePct === 100 ? t('union.dashboard.shared.profileCompleteCheck') : t('union.dashboard.shared.profilePercentComplete', { pct: unionProfilePct })}
            </p>
            <div style={{ height: '4px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ width: `${unionProfilePct}%`, height: '100%', backgroundColor: AMBER, borderRadius: '2px' }} />
            </div>
            {unionMissingItems.length > 0 && (
              <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {unionMissingItems.map(item => (
                  <button
                    key={item.label}
                    onClick={() => setActiveTab(item.tab)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '5px', fontFamily: FONT_BODY }}
                  >
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: AMBER, lineHeight: 1 }}>+</span>
                    <span style={{ fontSize: '0.72rem', color: AMBER, fontWeight: 500 }}>{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Upcoming interview(s) + quick actions */}
        <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {upcomingInterviews.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {upcomingInterviews.map((interview) => {
                const countdown = getInterviewCountdown(interview.interviewAt, interview.status, now);
                const accentColor = URGENCY_COLORS[countdown.urgency] || AMBER;
                return (
                  <div
                    key={interview.id}
                    onClick={() => openUpcomingInterview(interview)}
                    style={{ backgroundColor: NAVY, borderLeft: `3px solid ${accentColor}`, borderRadius: '0 8px 8px 0', padding: '10px 12px', cursor: 'pointer' }}
                  >
                    <p style={{ fontSize: '0.64rem', fontWeight: 700, color: AMBER, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '4px' }}>
                      {t('union.dashboard.shared.upcomingInterview')}
                    </p>
                    <p style={{ fontSize: '0.78rem', color: WHITE, fontWeight: 600, marginBottom: '2px', overflowWrap: 'break-word' }}>
                      {interview.otherPartyName}
                    </p>
                    <p style={{ fontSize: '0.72rem', color: countdown.urgency === 'normal' ? 'rgba(255,255,255,0.5)' : accentColor, fontWeight: countdown.urgency === 'normal' ? 400 : 700 }}>
                      {countdown.text}
                    </p>
                    {interview.status === 'accepted' && (
                      <p style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.68rem', color: '#10B981', fontWeight: 700, marginTop: '2px' }}><LineIcon name="check" size={16} />{t('union.dashboard.shared.confirmed')}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <button
            onClick={() => { setActiveTab('Apprenticeships'); openPostModal(); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'rgba(255,255,255,0.04)', border: 'none', cursor: 'pointer',
              padding: '9px 10px', borderRadius: '8px', width: '100%',
              color: WHITE, fontFamily: FONT_BODY, fontSize: '0.78rem', fontWeight: 600,
            }}
          >
            <IconBriefcase color={AMBER} />
            {t('union.dashboard.shared.postApprenticeshipButton')}
          </button>

          {unionAccount.slug && (
            <button
              onClick={handleCopyProfileLink}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                background: 'rgba(255,255,255,0.04)', border: 'none', cursor: 'pointer',
                padding: '9px 10px', borderRadius: '8px', width: '100%',
                color: linkCopied ? GREEN : WHITE, fontFamily: FONT_BODY, fontSize: '0.78rem', fontWeight: 600,
              }}
            >
              <LineIcon name="link" size={16} />
              {linkCopied ? t('union.dashboard.shared.linkCopied') : t('union.dashboard.shared.copyProfileLink')}
            </button>
          )}
        </div>

        {/* Sign out — pinned to bottom of sidebar */}
        <div style={{ marginTop: 'auto', padding: '14px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <HelpSidebarButton onClick={support.open} />
          <button
            onClick={handleSignOut}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '8px 10px', borderRadius: '8px', width: '100%',
              color: '#F87171', fontFamily: FONT_BODY, fontSize: '0.82rem', fontWeight: 600,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            {t('common.signOut')}
          </button>
        </div>

        {/* Legal links — pinned below Sign out */}
        <div style={{ padding: '0 14px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <LegalLinksRow fontSize="0.68rem" wrap />
          <p style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.25)', fontFamily: FONT_BODY }}>{t('common.copyright')}</p>
        </div>
      </aside>

      {/* ── Tab bar (desktop) ── */}
      <div className="ud-tab-bar" style={{
        position: 'fixed', top: NAV_H, left: SIDEBAR_W, right: 0, zIndex: 90,
        backgroundColor: NAVY, borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        <div
          ref={tabRowRef}
          className="ud-tab-scroll"
          onScroll={updateTabRowFade}
          style={{ display: 'flex', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
        >
        {TABS.map(tab => {
          const msgBadge  = tab === 'Messages' && unreadCount > 0;
          const candBadge = tab === 'Candidates' && newCandidatesCount > 0;
          return (
            <button
              key={tab}
              data-tab={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '12px 20px', fontSize: '0.86rem', fontWeight: 600,
                fontFamily: FONT_BODY, whiteSpace: 'nowrap', flexShrink: 0,
                color: activeTab === tab ? AMBER : 'rgba(255,255,255,0.4)',
                borderBottom: activeTab === tab ? `2px solid ${AMBER}` : '2px solid transparent',
                marginBottom: '-1px', position: 'relative',
              }}
            >
              {tabLabel(tab)}
              {(msgBadge || candBadge) && (
                <span style={{
                  position: 'absolute', top: '6px', right: '2px', minWidth: '16px', height: '16px', borderRadius: '8px',
                  backgroundColor: AMBER, color: NAVY, fontSize: '8px', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', lineHeight: 1,
                }}>
                  {msgBadge ? (unreadCount > 9 ? '9+' : unreadCount) : (newCandidatesCount > 9 ? '9+' : newCandidatesCount)}
                </span>
              )}
            </button>
          );
        })}
        </div>
        {tabRowFade.left && (
          <div aria-hidden="true" style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: '64px', pointerEvents: 'none', background: `linear-gradient(to left, rgba(13,27,42,0), ${NAVY} 85%)` }} />
        )}
        {tabRowFade.right && (
          <div aria-hidden="true" data-tab-fade="right" style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: '64px', pointerEvents: 'none', background: `linear-gradient(to right, rgba(13,27,42,0), ${NAVY} 85%)` }} />
        )}
      </div>

      {/* ── Main content ── */}
      <main className="ud-main" style={{
        display: 'flex', flexDirection: 'column',
        marginLeft: SIDEBAR_W,
        height: '100dvh', boxSizing: 'border-box',
        padding: `${NAV_H + 49}px 24px 40px`,
      }}>

        {/* ── DASHBOARD ── */}
        {activeTab === 'Dashboard' && (
          <div>
            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>
              {t('union.dashboard.home.header')}
            </p>

            {userId && (
              <ProfileCompletionBanner
                storageKey={`profileBannerDismissed_union_${userId}`}
                profileLabel={t('union.dashboard.shared.unionProfileLabel')}
                missingItems={unionMissingItems}
                pct={unionProfilePct}
                onNavigate={setActiveTab}
              />
            )}

            {/* Upcoming interview(s) -- mobile only, since the sidebar (where this also lives) is hidden below 768px */}
            {isMobile && upcomingInterviews.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                {upcomingInterviews.map((interview) => {
                  const countdown = getInterviewCountdown(interview.interviewAt, interview.status, now);
                  const accentColor = URGENCY_COLORS[countdown.urgency] || AMBER;
                  return (
                    <div
                      key={interview.id}
                      onClick={() => openUpcomingInterview(interview)}
                      style={{ backgroundColor: NAVY_MID, borderLeft: `3px solid ${accentColor}`, borderRadius: '0 10px 10px 0', padding: '12px 16px', cursor: 'pointer' }}
                    >
                      <p style={{ fontSize: '0.66rem', fontWeight: 700, color: AMBER, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '4px' }}>
                        {t('union.dashboard.shared.upcomingInterview')}
                      </p>
                      <p style={{ fontSize: '0.85rem', color: WHITE, fontWeight: 600, marginBottom: '2px' }}>
                        {interview.otherPartyName}
                      </p>
                      <p style={{ fontSize: '0.78rem', color: countdown.urgency === 'normal' ? 'rgba(255,255,255,0.5)' : accentColor, fontWeight: countdown.urgency === 'normal' ? 400 : 700 }}>
                        {countdown.text}
                      </p>
                      {interview.status === 'accepted' && (
                        <p style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem', color: '#10B981', fontWeight: 700, marginTop: '2px' }}><LineIcon name="check" size={16} />{t('union.dashboard.shared.confirmed')}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Stat cards -- mirrors the Employer dashboard's shape (Applicants / Active
                postings / Upcoming interviews) but swaps "Hired" for "Messages", since
                apprenticeship_applications.status never reaches a "hired" state for a union
                account (only pending/reviewing/accepted/rejected) -- Messages is what's
                actually meaningful and queryable here instead. Every trend is a real
                computed "+N this week", never a placeholder. */}
            <div className="ud-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '28px' }}>
              <StatCard
                label={t('union.dashboard.home.statApplicants')}
                value={candidates.length}
                Icon={IconUsers}
                iconColor={AMBER}
                trend={countThisWeek(candidates) > 0 ? t('employer.dashboard.home.trendThisWeek', { count: countThisWeek(candidates) }) : null}
                onClick={() => { setCandidatesArchiveFilter('active'); setActiveTab('Candidates'); }}
              />
              <StatCard
                label={t('union.dashboard.home.statActivePostings')}
                value={apprenticeships.filter(a => a.status === 'active').length}
                Icon={IconBriefcase}
                iconColor={AMBER}
                trend={countThisWeek(apprenticeships.filter(a => a.status === 'active')) > 0 ? t('employer.dashboard.home.trendThisWeek', { count: countThisWeek(apprenticeships.filter(a => a.status === 'active')) }) : null}
              />
              <StatCard
                label={t('union.dashboard.home.statUpcomingInterviews')}
                value={upcomingInterviewsCount}
                Icon={IconCalendar}
                iconColor={AMBER}
                trend={interviewsScheduledThisWeek > 0 ? t('employer.dashboard.home.trendThisWeek', { count: interviewsScheduledThisWeek }) : null}
              />
              <StatCard
                label={t('union.dashboard.home.statMessages')}
                value={unreadCount}
                Icon={IconMsg}
                iconColor={AMBER}
                trend={messagesThisWeek > 0 ? t('employer.dashboard.home.trendThisWeek', { count: messagesThisWeek }) : null}
                onClick={() => setActiveTab('Messages')}
              />
            </div>

            <div style={{ marginBottom: '28px' }}>
              <ActivityFeed accountType="union" accountId={userId} refreshKey={refreshKey} />
            </div>

            {/* Only for unions with zero apprenticeships posted (any status) -- gated on
                apprenticeshipsLoaded so this doesn't flash for a union that has postings
                while the fetch is still in flight. */}
            {apprenticeshipsLoaded && apprenticeships.length === 0 && (
              <div style={{ backgroundColor: NAVY_MID, borderRadius: '14px', padding: '32px', maxWidth: '640px' }}>
                <h2 style={{ fontFamily: FONT_HEAD, fontSize: '1.8rem', color: AMBER, letterSpacing: '0.03em', marginBottom: '10px' }}>
                  {t('union.dashboard.home.welcomeTitle', { unionName: unionAccount.union_name })}
                </h2>
                <p style={{ fontSize: '0.92rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, marginBottom: '20px' }}>
                  {t('union.dashboard.home.welcomeBody')}
                </p>
                <button
                  onClick={() => { setActiveTab('Apprenticeships'); openPostModal(); }}
                  style={{
                    background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.9rem',
                    padding: '11px 22px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY,
                  }}
                >
                  {t('union.dashboard.home.postApprenticeshipArrowButton')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── APPRENTICESHIPS ── */}
        {activeTab === 'Apprenticeships' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
              <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                {t('union.dashboard.apprenticeships.header')}
              </p>
              <button
                onClick={openPostModal}
                style={{
                  background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.85rem',
                  padding: '10px 18px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY,
                }}
              >
                {t('union.dashboard.apprenticeships.postNewButton')}
              </button>
            </div>

            {/* Active / Closed / Archived filter tabs */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '4px', width: 'fit-content' }}>
              {['active', 'closed', 'archived'].map(f => (
                <button
                  key={f}
                  onClick={() => setApprArchiveFilter(f)}
                  style={{
                    background: apprArchiveFilter === f ? AMBER : 'transparent',
                    color: apprArchiveFilter === f ? NAVY : 'rgba(255,255,255,0.6)',
                    border: 'none', borderRadius: '6px', padding: '6px 14px',
                    fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: FONT_BODY,
                    textTransform: 'capitalize',
                  }}
                >
                  {t(`union.dashboard.shared.filter${f.charAt(0).toUpperCase()}${f.slice(1)}`)}
                </button>
              ))}
            </div>

            {/* Search / sort row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: '1 1 200px', minWidth: '170px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  placeholder={t('union.dashboard.apprenticeships.searchPlaceholder')}
                  value={apprSearchQuery}
                  onChange={e => setApprSearchQuery(e.target.value)}
                  style={{ ...inp, paddingLeft: '30px' }}
                />
              </div>
              <select
                value={apprSortBy}
                onChange={e => setApprSortBy(e.target.value)}
                style={{ ...sel, width: 'auto' }}
              >
                <option value="newest">{t('employer.dashboard.home.sortNewest')}</option>
                <option value="oldest">{t('employer.dashboard.home.sortOldest')}</option>
                <option value="applicants">{t('employer.dashboard.home.sortApplicants')}</option>
              </select>
            </div>

            {(() => {
              const applicantCountByAppr = candidates.reduce((acc, c) => {
                acc[c.apprenticeship_id] = (acc[c.apprenticeship_id] || 0) + 1;
                return acc;
              }, {});
              const hasNewApplicantByAppr = candidates.reduce((acc, c) => {
                if (Date.now() - new Date(c.created_at).getTime() < 24 * 60 * 60 * 1000) acc[c.apprenticeship_id] = true;
                return acc;
              }, {});
              const apprenticeshipsForStatus = apprenticeships.filter(appr => {
                if (apprArchiveFilter === 'archived') return !!appr.archived_at;
                if (apprArchiveFilter === 'closed') return appr.status === 'closed' && !appr.archived_at;
                return appr.status !== 'closed' && !appr.archived_at;
              });
              const apprenticeshipsForView = apprenticeshipsForStatus
                .filter(appr => {
                  if (!apprSearchQuery.trim()) return true;
                  const q = apprSearchQuery.toLowerCase();
                  return (appr.title || '').toLowerCase().includes(q) || (appr.trade || '').toLowerCase().includes(q);
                })
                .sort((a, b) => {
                  if (apprSortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
                  if (apprSortBy === 'applicants') return (applicantCountByAppr[b.id] || 0) - (applicantCountByAppr[a.id] || 0);
                  return new Date(b.created_at) - new Date(a.created_at);
                });
              return apprenticeshipsForView.length === 0 ? (
              <div style={{ background: NAVY_MID, borderRadius: '8px', padding: '32px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
                {apprenticeshipsForStatus.length > 0
                  ? t('union.dashboard.apprenticeships.noPostingsMatch')
                  : apprArchiveFilter === 'archived' ? t('union.dashboard.apprenticeships.noArchivedPostings') : apprArchiveFilter === 'closed' ? t('union.dashboard.apprenticeships.noClosedPostings') : t('union.dashboard.apprenticeships.noPostingsYet')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {apprenticeshipsForView.map(appr => {
                  const { label: statusLabel, color: statusColor } = apprenticeshipStatusBadge(appr.status, t);
                  const isArchived = !!appr.archived_at;
                  const applicantCount = applicantCountByAppr[appr.id] || 0;
                  const daysOpen       = Math.floor((Date.now() - new Date(appr.created_at).getTime()) / (24 * 60 * 60 * 1000));
                  const isResolved     = appr.status === 'closed' || isArchived;
                  const needsAttention = !isResolved && applicantCount === 0 && daysOpen > 14;
                  const accentColor    = (isResolved || applicantCount > 0) ? GREEN : (needsAttention ? ATTENTION_RED : AMBER);
                  const hasNewToday    = !!hasNewApplicantByAppr[appr.id];
                  return (
                    <div key={appr.id} style={{ background: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderLeft: `3px solid ${accentColor}`, borderRadius: '0 10px 10px 0', padding: '18px 20px', opacity: isArchived ? 0.7 : 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '220px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: WHITE }}>{appr.title}</h3>
                            <span style={{ backgroundColor: 'rgba(245,166,35,0.15)', color: AMBER, borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '3px 10px' }}>
                              {appr.trade}
                            </span>
                            <span style={{ backgroundColor: statusColor, color: statusColor === AMBER ? NAVY : WHITE, borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '3px 10px' }}>
                              {statusLabel}
                            </span>
                            {appr.gi_bill_eligible && (
                              <span style={{ backgroundColor: 'rgba(59,130,246,0.15)', color: '#3B82F6', borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '3px 10px' }}>
                                {t('union.dashboard.apprenticeships.giBillEligibleBadge')}
                              </span>
                            )}
                            {appr.drug_screening_required && (
                              <span data-testid="drug-screening-badge" style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: WHITE, border: '1px solid rgba(255,255,255,0.28)', borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '3px 10px' }}>
                                {t('marketing.trades.drugScreeningRequired')}
                              </span>
                            )}
                            {isArchived && (
                              <span style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '3px 10px' }}>
                                {t('union.dashboard.apprenticeships.archivedBadge')}
                              </span>
                            )}
                            {needsAttention && (
                              <span style={{ backgroundColor: 'rgba(226,75,74,0.15)', color: ATTENTION_RED_TEXT, borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '3px 10px' }}>
                                {t('union.dashboard.apprenticeships.needsAttentionBadge')}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)' }}>
                            {appr.start_date && <span>{t('union.dashboard.apprenticeships.startLabel', { date: appr.start_date })}</span>}
                            {appr.hourly_rate_start && (
                              <span>${appr.hourly_rate_start}{appr.hourly_rate_end ? ` – $${appr.hourly_rate_end}` : ''}/hr</span>
                            )}
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                              {t('union.dashboard.apprenticeships.applicantCount', { count: applicantCount })}
                              {hasNewToday && (
                                <span style={{ fontSize: '11px', color: TREND_GREEN, fontWeight: 600 }}>{t('union.dashboard.apprenticeships.newTodayBadge')}</span>
                              )}
                              {needsAttention && (
                                <span style={{ fontSize: '11px', color: ATTENTION_RED_TEXT, fontWeight: 600 }}>{t('union.dashboard.apprenticeships.daysOpenBadge', { count: daysOpen })}</span>
                              )}
                            </span>
                          </div>
                          {(appr.description || (appr.requirements || []).length > 0) && (
                            <button
                              onClick={() => setExpandedAppr(expandedAppr === appr.id ? null : appr.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: AMBER, fontFamily: FONT_BODY, padding: '8px 0 0', display: 'block' }}
                            >
                              {expandedAppr === appr.id ? t('union.dashboard.apprenticeships.hideDetails') : t('union.dashboard.apprenticeships.viewDetails')}
                            </button>
                          )}
                          {expandedAppr === appr.id && (
                            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                              {appr.description && (
                                <div style={{ marginBottom: (appr.requirements || []).length > 0 ? '12px' : 0 }}>
                                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('union.dashboard.apprenticeships.descriptionLabel')}</div>
                                  <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>{renderJobDescription(appr.description)}</div>
                                </div>
                              )}
                              {(appr.requirements || []).length > 0 && (
                                <div>
                                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('union.dashboard.apprenticeships.requirementsLabel')}</div>
                                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>
                                    {appr.requirements.map((r, i) => <li key={i}>{r}</li>)}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                          <button
                            onClick={() => openEditModal(appr)}
                            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', fontWeight: 600, padding: '7px 14px', borderRadius: '7px', cursor: 'pointer', fontFamily: FONT_BODY }}
                          >
                            {t('union.dashboard.shared.editButton')}
                          </button>
                          <button
                            onClick={() => openDuplicateModal(appr)}
                            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', fontWeight: 600, padding: '7px 14px', borderRadius: '7px', cursor: 'pointer', fontFamily: FONT_BODY }}
                          >
                            {t('union.dashboard.shared.duplicateButton')}
                          </button>
                          {appr.status !== 'closed' && !isArchived && (
                            <button
                              onClick={() => handleCloseApprenticeship(appr)}
                              style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.35)', color: '#F87171', fontSize: '0.8rem', fontWeight: 600, padding: '7px 14px', borderRadius: '7px', cursor: 'pointer', fontFamily: FONT_BODY }}
                            >
                              {t('union.dashboard.shared.closeButton')}
                            </button>
                          )}
                          {!isArchived ? (
                            <button
                              onClick={() => handleArchiveApprenticeship(appr)}
                              style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', fontWeight: 600, padding: '7px 14px', borderRadius: '7px', cursor: 'pointer', fontFamily: FONT_BODY }}
                            >
                              {t('union.dashboard.shared.archiveButton')}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleUnarchiveApprenticeship(appr)}
                              style={{ background: 'transparent', border: `1px solid ${AMBER}`, color: AMBER, fontSize: '0.8rem', fontWeight: 600, padding: '7px 14px', borderRadius: '7px', cursor: 'pointer', fontFamily: FONT_BODY }}
                            >
                              {t('union.dashboard.shared.unarchiveButton')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
            })()}
          </div>
        )}

        {/* ── EXAMS & REQUIREMENTS ── */}
        {activeTab === 'Exams' && (
          <div>
            <UnionExamsPanel
              userId={userId}
              unionAccount={unionAccount}
              apprenticeships={apprenticeships}
              onApprenticeshipUpdated={(id, patch) => setApprenticeships(prev => prev.map(a => a.id === id ? { ...a, ...patch } : a))}
              onPostApprenticeship={() => { setActiveTab('Apprenticeships'); openPostModal(); }}
            />
            <div className="ud-mobile-spacer" />
          </div>
        )}

        {/* ── CANDIDATES ── */}
        {activeTab === 'Candidates' && (
          <div>
            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>
              {t('union.dashboard.candidates.header')}
            </p>

            {/* Active / Archived filter tabs */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '4px', width: 'fit-content' }}>
              {['active', 'archived'].map(f => (
                <button
                  key={f}
                  onClick={() => setCandidatesArchiveFilter(f)}
                  style={{
                    background: candidatesArchiveFilter === f ? AMBER : 'transparent',
                    color: candidatesArchiveFilter === f ? NAVY : 'rgba(255,255,255,0.6)',
                    border: 'none', borderRadius: '6px', padding: '6px 14px',
                    fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: FONT_BODY,
                    textTransform: 'capitalize',
                  }}
                >
                  {t(`union.dashboard.shared.filter${f.charAt(0).toUpperCase()}${f.slice(1)}`)}
                </button>
              ))}
            </div>

            <div style={{ marginBottom: '14px' }}>
              <CompareEntryButton
                count={applicantCompare.ids.length}
                label={t('compare.union.entryButton')}
                onClick={() => setShowApplicantCompare(true)}
              />
            </div>

            {(() => {
              const candidatesForView = candidates.filter(c =>
                candidatesArchiveFilter === 'archived' ? !!c.archived_at : !c.archived_at
              );
              if (candidatesForView.length === 0) return (
                <div style={{ background: NAVY_MID, borderRadius: '8px', padding: '32px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
                  {candidatesArchiveFilter === 'archived' ? t('union.dashboard.candidates.noneArchived') : t('union.dashboard.candidates.noneYet')}
                </div>
              );
              const examCounts = candidatesForView.reduce((acc, c) => {
                const r = c.review?.exam_result;
                acc[r === 'passed' || r === 'failed' ? r : 'notSet'] += 1;
                return acc;
              }, { passed: 0, failed: 0, notSet: 0 });
              const chip = (key, label, color) => (
                <span key={key} data-review-chip={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '999px', padding: '4px 10px', fontSize: '0.74rem', fontWeight: 600, color: WHITE, whiteSpace: 'nowrap' }}>
                  <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: color, flexShrink: 0 }} />
                  {label}
                </span>
              );
              const examPill = (result) => {
                const map = {
                  passed: { bg: GREEN, key: 'examPassed' },
                  failed: { bg: RED, key: 'examFailed' },
                };
                const m = map[result];
                return (
                  <span data-review="result-pill" style={{ background: m ? m.bg : 'rgba(255,255,255,0.08)', color: m ? WHITE : 'rgba(255,255,255,0.55)', borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '2px 8px', whiteSpace: 'nowrap' }}>
                    {t(`union.dashboard.candidates.review.${m ? m.key : 'pillNotSet'}`)}
                  </span>
                );
              };
              const actionBtnSize = isMobile ? { minHeight: '48px', padding: '0 14px' } : {};
              return (
              <>
                {/* Exams happen in person; staff record the outcome here. Summary computed from
                    the loaded (persisted) reviews of the applicants in this view. */}
                <div style={{ marginBottom: '14px' }}>
                  <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, marginBottom: '10px' }}>
                    {t('union.dashboard.candidates.review.intro')}
                  </p>
                  <div data-review="summary" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {chip('applicants', t('union.dashboard.candidates.review.chipApplicants', { count: candidatesForView.length }), AMBER)}
                    {chip('passed', t('union.dashboard.candidates.review.chipPassed', { count: examCounts.passed }), GREEN)}
                    {chip('failed', t('union.dashboard.candidates.review.chipFailed', { count: examCounts.failed }), RED)}
                    {chip('notSet', t('union.dashboard.candidates.review.chipNotSet', { count: examCounts.notSet }), 'rgba(255,255,255,0.35)')}
                  </div>
                </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {candidatesForView.map(candidate => {
                  const status = candidate.status || 'pending';
                  const { label: badgeLabel, color: badgeColor } = candidateStatusBadge(status, t);
                  const isNotifTarget = highlightWorkerId === candidate.worker_id;
                  const review = candidate.review || {};
                  const reqTotal = requirementsTotal(candidate.apprenticeship);
                  const expanded = expandedCandidateIds.has(candidate.id);
                  const toggleExpanded = () => setExpandedCandidateIds(prev => {
                    const next = new Set(prev);
                    if (next.has(candidate.id)) next.delete(candidate.id); else next.add(candidate.id);
                    return next;
                  });
                  const statusBadge = (
                    <span style={{ backgroundColor: badgeColor, color: badgeColor === AMBER ? NAVY : WHITE, borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '2px 8px', whiteSpace: 'nowrap' }}>
                      {badgeLabel}
                    </span>
                  );
                  const compareToggle = (
                    <CompareToggle
                      selected={applicantCompare.isSelected(candidate.id)}
                      onToggle={() => applicantCompare.toggle(candidate.id)}
                      addLabel={t('compare.addToCompare')}
                      removeLabel={t('compare.removeFromCompare')}
                    />
                  );
                  const appliedFor = (
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', overflowWrap: 'anywhere' }}>
                      {t('union.dashboard.candidates.appliedForPrefix')} {candidate.apprenticeship?.title || t('union.dashboard.candidates.apprenticeshipFallback')}
                      {candidate.created_at && ` · ${new Date(candidate.created_at).toLocaleDateString()}`}
                    </div>
                  );
                  const tradeAndCity = (candidate.trade || candidate.profile?.city) && (
                    <>
                      {candidate.trade && (
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)' }}>{candidate.trade}</span>
                      )}
                      {candidate.profile?.city && (
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>{candidate.trade ? '· ' : ''}{joinParts([candidate.profile.city, candidate.profile.state], ', ')}</span>
                      )}
                    </>
                  );
                  const actions = (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <select
                        value={status}
                        onChange={e => handleUpdateCandidateStatus(candidate, e.target.value)}
                        style={{ ...sel, padding: '6px 10px', fontSize: '0.8rem', width: 'auto', ...(isMobile ? { minHeight: '48px' } : {}) }}
                      >
                        <option value="pending">{t('union.dashboard.candidates.statusPending')}</option>
                        <option value="reviewing">{t('union.dashboard.candidates.statusReviewing')}</option>
                        <option value="accepted">{t('union.dashboard.candidates.statusAccepted')}</option>
                        <option value="rejected">{t('union.dashboard.candidates.statusRejected')}</option>
                      </select>
                      <button
                        onClick={() => navigate(`/worker/profile/${candidate.worker_id}?apprenticeshipId=${candidate.apprenticeship_id}`)}
                        style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', fontSize: '11px', padding: '7px 12px', borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY, ...actionBtnSize }}
                      >
                        {t('union.dashboard.candidates.viewProfileButton')}
                      </button>
                      <button
                        onClick={() => handleMessageCandidate(candidate)}
                        style={{ background: AMBER, border: 'none', color: NAVY, fontSize: '11px', fontWeight: 700, padding: '7px 12px', borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY, ...actionBtnSize }}
                      >
                        {t('union.dashboard.candidates.messageButton')}
                      </button>
                      {!candidate.archived_at ? (
                        <button
                          onClick={() => handleArchiveCandidate(candidate.id)}
                          style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.45)', fontSize: '10px', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY, ...actionBtnSize }}
                        >
                          {t('union.dashboard.shared.archiveButton')}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUnarchiveCandidate(candidate.id)}
                          style={{ background: 'transparent', border: `1px solid ${AMBER}`, color: AMBER, fontSize: '10px', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY, ...actionBtnSize }}
                        >
                          {t('union.dashboard.shared.unarchiveButton')}
                        </button>
                      )}
                    </div>
                  );
                  const reviewControls = (blockStyles) => (
                    <ApplicantReviewControls
                      supabase={supabase}
                      applicationId={candidate.id}
                      review={review}
                      total={reqTotal}
                      onPersisted={handleReviewPersisted}
                      isMobile={isMobile}
                      blockStyles={blockStyles}
                    />
                  );
                  const cardStyle = {
                    background: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px',
                    padding: isMobile ? '6px 12px' : '14px 16px', minWidth: 0,
                    boxShadow: isNotifTarget ? `0 0 0 3px ${AMBER}` : 'none', transition: 'box-shadow 0.3s',
                  };

                  if (isMobile) {
                    // Collapsed: name, status, exam result and "N of M met"; tap for everything.
                    return (
                      <div key={candidate.id} data-worker-id={candidate.worker_id} data-application-id={candidate.id} style={cardStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {compareToggle}
                          <button
                            type="button"
                            onClick={toggleExpanded}
                            aria-expanded={expanded}
                            aria-label={t(expanded ? 'union.dashboard.candidates.review.hideDetails' : 'union.dashboard.candidates.review.showDetails')}
                            data-review="expand"
                            style={{ flex: 1, minWidth: 0, minHeight: '56px', display: 'flex', alignItems: 'center', gap: '10px', background: 'none', border: 'none', padding: '6px 0', cursor: 'pointer', textAlign: 'left', fontFamily: FONT_BODY, color: WHITE }}
                          >
                            <span style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ display: 'block', fontSize: '14px', fontWeight: 600, overflowWrap: 'anywhere' }}>
                                {candidate.worker?.first_name} {candidate.worker?.last_name}
                              </span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                                {statusBadge}
                                {examPill(review.exam_result)}
                                {reqTotal > 0 && (
                                  <span data-review="met-summary" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap' }}>
                                    {t('compare.union.metOfTotal', { met: review.requirements_met ?? t('union.dashboard.candidates.review.metUnset'), total: reqTotal })}
                                  </span>
                                )}
                              </span>
                            </span>
                            <span aria-hidden="true" style={{ color: AMBER, fontSize: '12px', flexShrink: 0, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
                          </button>
                        </div>
                        {expanded && (
                          <div style={{ padding: '4px 0 12px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap', margin: '10px 0 2px' }}>{tradeAndCity}</div>
                            {appliedFor}
                            {reviewControls({ exam: { marginTop: '16px' }, requirements: { marginTop: '16px' }, notes: { marginTop: '16px' } })}
                            <div style={{ marginTop: '8px' }}>{actions}</div>
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Desktop: one row per applicant -- applicant + status | exam result |
                  // requirements | notes, wrapping onto further lines when the row runs out of room.
                  return (
                    <div key={candidate.id} data-worker-id={candidate.worker_id} data-application-id={candidate.id} style={cardStyle}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '14px 22px' }}>
                        <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {compareToggle}
                            <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: AMBER, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, color: NAVY, flexShrink: 0 }}>
                              {candidate.worker?.first_name?.[0]}{candidate.worker?.last_name?.[0]}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap', marginBottom: '2px' }}>
                                <span style={{ fontSize: '13px', fontWeight: 600, color: WHITE }}>
                                  {candidate.worker?.first_name} {candidate.worker?.last_name}
                                </span>
                                {tradeAndCity}
                                {statusBadge}
                              </div>
                              {appliedFor}
                            </div>
                          </div>
                          <div style={{ marginTop: '12px' }}>{actions}</div>
                        </div>
                        {reviewControls({ exam: { flex: '0 0 auto' }, requirements: { flex: '0 0 auto' }, notes: { flex: '1 1 240px', minWidth: '220px' } })}
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
            );
            })()}
          </div>
        )}

        {support.modal}

        {/* ── MESSAGES ── */}
        {activeTab === 'Messages' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div className="ud-messages-wrap" style={{ flex: 1, overflow: 'hidden' }}>
              <Messaging
                key={openConversationId || 'msgs'}
                supabase={supabase}
                userId={userId}
                userType="union"
                actingUserId={myUserId}
                initialConversationId={openConversationId}
                senderName={unionAccount.union_name}
                support={support}
                onUnreadCountChange={(value, isAbsolute) => {
                  if (isAbsolute) setUnreadCount(value);
                  else setUnreadCount(prev => prev + value);
                }}
              />
            </div>
          </div>
        )}

        {/* ── PROFILE ── */}
        {activeTab === 'Profile' && profileForm && (
          <div style={{ maxWidth: '640px' }}>
            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>
              {t('union.dashboard.profile.header')}
            </p>

            {!isOwner && (
              <p data-owner-only-note style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5, backgroundColor: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.3)', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px' }}>
                {t('union.dashboard.profile.ownerOnlyNote')}
              </p>
            )}

            {/* Union identity fields are owner-only at the DB layer (union_accounts UPDATE and the
                company-logos bucket both key on the owner's own uid), so for staff they render
                read-only via a disabled fieldset. Pay scale and benefits below stay editable --
                their tables accept any active union staff. */}
            <fieldset disabled={!isOwner} style={{ border: 0, padding: 0, margin: 0, minWidth: 0, opacity: isOwner ? 1 : 0.6 }}>

            {/* Logo drag-and-drop */}
            <div
              onDragOver={e => { e.preventDefault(); if (isOwner) setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={e => {
                e.preventDefault();
                setDragActive(false);
                if (!isOwner) return;
                const file = e.dataTransfer.files?.[0];
                if (file) handleLogoFile(file);
              }}
              onClick={() => { if (isOwner) logoInputRef.current?.click(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '16px',
                border: `2px dashed ${dragActive ? AMBER : 'rgba(255,255,255,0.15)'}`,
                borderRadius: '12px', padding: '20px', marginBottom: '20px',
                backgroundColor: dragActive ? 'rgba(245,166,35,0.06)' : 'transparent',
                cursor: 'pointer', transition: 'background-color 0.15s, border-color 0.15s',
              }}
            >
              <UnionAvatar logoUrl={unionAccount.logo_url} initials={unionAccount.union_name?.[0]} size={64} />
              <div>
                <p style={{ fontSize: '0.88rem', fontWeight: 600, color: WHITE, marginBottom: '3px' }}>
                  {logoUploading ? t('union.dashboard.profile.uploadingLabel') : t('union.dashboard.profile.dragDropLabel')}
                </p>
                <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)' }}>{t('union.dashboard.profile.orClickBrowse')}</p>
                {logoMsg && (
                  <p style={{ fontSize: '0.78rem', fontWeight: 600, color: logoMsgIsError ? '#F87171' : GREEN, marginTop: '4px' }}>
                    {logoMsg}
                  </p>
                )}
              </div>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) handleLogoFile(f); e.target.value = ''; }}
              />
            </div>

            <div style={fld}>
              <label style={lbl}>{t('union.dashboard.profile.unionNameLabel')}</label>
              <input style={inp} value={profileForm.union_name} onChange={e => setProfileForm(p => ({ ...p, union_name: e.target.value }))} />
            </div>

            <div style={g2}>
              <div>
                <label style={lbl}>{t('union.dashboard.profile.localNumberLabel')}</label>
                <input style={inp} value={profileForm.local_number} onChange={e => setProfileForm(p => ({ ...p, local_number: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>{t('union.dashboard.shared.tradeLabel')}</label>
                <select style={sel} value={profileForm.trade} onChange={e => setProfileForm(p => ({ ...p, trade: e.target.value }))}>
                  <option value="">{t('union.dashboard.shared.tradePlaceholder')}</option>
                  {TRADES.map(tr => <option key={tr.value} value={tr.value}>{t(`worker.tradeNames.${tr.key}`)}</option>)}
                </select>
              </div>
            </div>

            <div style={g2}>
              <div>
                <label style={lbl}>{t('union.dashboard.profile.phoneLabel')}</label>
                <input style={inp} type="tel" value={profileForm.phone} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>{t('union.dashboard.profile.websiteLabel')}</label>
                <input style={inp} value={profileForm.website} onChange={e => setProfileForm(p => ({ ...p, website: e.target.value }))} />
              </div>
            </div>

            <div style={fld}>
              <label style={lbl}>{t('union.dashboard.profile.addressLabel')}</label>
              <input style={inp} value={profileForm.address} onChange={e => setProfileForm(p => ({ ...p, address: e.target.value }))} />
            </div>

            <div style={g2}>
              <div>
                <label style={lbl}>{t('union.dashboard.shared.cityLabel')}</label>
                <input style={inp} value={profileForm.city} onChange={e => setProfileForm(p => ({ ...p, city: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>{t('union.dashboard.shared.stateLabel')}</label>
                <select style={sel} value={profileForm.state} onChange={e => setProfileForm(p => ({ ...p, state: e.target.value }))}>
                  <option value="IL">Illinois</option>
                  <option value="IN">Indiana</option>
                  <option value="WI">Wisconsin</option>
                  <option value="MI">Michigan</option>
                  <option value="OH">Ohio</option>
                  <option value="MO">Missouri</option>
                  <option value="IA">Iowa</option>
                  <option value="MN">Minnesota</option>
                </select>
              </div>
            </div>

            <div style={fld}>
              <label style={lbl}>{t('union.dashboard.profile.aboutLabel')}</label>
              <textarea style={txt} value={profileForm.about} onChange={e => setProfileForm(p => ({ ...p, about: e.target.value }))} />
            </div>

            <div style={fld}>
              <label style={lbl}>{t('union.dashboard.profile.howToApplyLabel')}</label>
              <textarea style={txt} value={profileForm.how_to_apply} onChange={e => setProfileForm(p => ({ ...p, how_to_apply: e.target.value }))} />
            </div>

            {isOwner && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
                <button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.88rem', padding: '10px 22px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY, opacity: savingProfile ? 0.7 : 1 }}
                >
                  {savingProfile ? t('union.dashboard.shared.savingButton') : t('union.dashboard.profile.saveProfileButton')}
                </button>
                {profileMsg && <span style={{ fontSize: '0.84rem', fontWeight: 600, color: profileMsgIsError ? '#F87171' : GREEN }}>{profileMsg}</span>}
              </div>
            )}
            </fieldset>

            {unionAccount.slug && (
              <a
                href={`/unions/${unionAccount.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '0.85rem', color: AMBER, fontWeight: 600, textDecoration: 'none' }}
              >
                {t('union.dashboard.profile.viewPublicProfileLink')}
              </a>
            )}

            {/* Videos -- union_portfolio. Outside the owner-only fieldset: its RLS accepts any
                active union staff, same as pay scale and benefits below. */}
            <div style={{ marginTop: '36px', paddingTop: '28px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <PortfolioManager
                table="union_portfolio"
                ownerColumn="union_id"
                ownerId={userId}
                bucket="union-videos"
                subtitle={t('portfolio.unionSubtitle')}
              />
            </div>

            {/* Pay Scale editor */}
            <div style={{ marginTop: '36px', paddingTop: '28px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 style={{ fontWeight: 700, fontSize: '1.1rem', color: WHITE, marginBottom: '4px' }}>{t('union.dashboard.profile.payScaleHeading')}</h3>
              <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginBottom: '16px' }}>
                {t('union.dashboard.profile.payScaleHint')}
              </p>

              {payScaleRows.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
                  {payScaleRows.map((row, idx) => (
                    <div key={row.id ?? `new-pay-${idx}`} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <input
                        style={{ ...inp, flex: 2 }}
                        placeholder={t('union.dashboard.profile.payLevelPlaceholder')}
                        value={row.level}
                        onChange={e => updatePayScaleRow(idx, 'level', e.target.value)}
                      />
                      <input
                        style={{ ...inp, flex: 1 }}
                        type="number"
                        step="0.01"
                        placeholder={t('union.dashboard.profile.hourlyRatePlaceholder')}
                        value={row.hourly_rate}
                        onChange={e => updatePayScaleRow(idx, 'hourly_rate', e.target.value)}
                      />
                      <button
                        onClick={() => deletePayScaleRow(idx)}
                        aria-label={t('union.dashboard.profile.removePayRate')}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.35)', color: '#F87171', width: '44px', height: '44px', borderRadius: '8px', cursor: 'pointer', flexShrink: 0, padding: 0 }}
                      >
                        <LineIcon name="x" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={addPayScaleRow}
                disabled={!profileRowsLoaded}
                style={{ display: 'block', background: 'transparent', border: `1.5px dashed ${AMBER}`, color: AMBER, fontWeight: 700, fontSize: '0.84rem', padding: '9px 16px', borderRadius: '8px', cursor: profileRowsLoaded ? 'pointer' : 'default', fontFamily: FONT_BODY, marginBottom: '14px', opacity: profileRowsLoaded ? 1 : 0.5 }}
              >
                {t('union.dashboard.profile.addPayLevelButton')}
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <button
                  onClick={handleSavePayScale}
                  disabled={savingPayScale || !profileRowsLoaded}
                  style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.86rem', padding: '10px 20px', borderRadius: '8px', cursor: profileRowsLoaded ? 'pointer' : 'default', fontFamily: FONT_BODY, opacity: (savingPayScale || !profileRowsLoaded) ? 0.7 : 1 }}
                >
                  {!profileRowsLoaded ? t('union.dashboard.shared.loadingButton')
                    : savingPayScale ? t('union.dashboard.shared.savingButton')
                    : t('union.dashboard.profile.savePayScaleButton')}
                </button>
                {payScaleMsg && <span style={{ fontSize: '0.84rem', fontWeight: 600, color: GREEN }}>{payScaleMsg}</span>}
              </div>
            </div>

            {/* Benefits editor */}
            <div style={{ marginTop: '36px', paddingTop: '28px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 style={{ fontWeight: 700, fontSize: '1.1rem', color: WHITE, marginBottom: '4px' }}>{t('union.dashboard.profile.benefitsHeading')}</h3>
              <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginBottom: '16px' }}>
                {t('union.dashboard.profile.benefitsHint')}
              </p>

              {benefitRows.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
                  {benefitRows.map((row, idx) => (
                    <div key={row.id ?? `new-benefit-${idx}`} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <input
                          style={inp}
                          placeholder={t('union.dashboard.profile.benefitNamePlaceholder')}
                          value={row.benefit}
                          onChange={e => updateBenefitRow(idx, 'benefit', e.target.value)}
                        />
                        <input
                          style={inp}
                          placeholder={t('union.dashboard.profile.benefitDescPlaceholder')}
                          value={row.description}
                          onChange={e => updateBenefitRow(idx, 'description', e.target.value)}
                        />
                      </div>
                      <button
                        onClick={() => deleteBenefitRow(idx)}
                        aria-label={t('union.dashboard.profile.removeBenefit')}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.35)', color: '#F87171', width: '44px', height: '44px', borderRadius: '8px', cursor: 'pointer', flexShrink: 0, padding: 0 }}
                      >
                        <LineIcon name="x" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={addBenefitRow}
                disabled={!profileRowsLoaded}
                style={{ display: 'block', background: 'transparent', border: `1.5px dashed ${AMBER}`, color: AMBER, fontWeight: 700, fontSize: '0.84rem', padding: '9px 16px', borderRadius: '8px', cursor: profileRowsLoaded ? 'pointer' : 'default', fontFamily: FONT_BODY, marginBottom: '14px', opacity: profileRowsLoaded ? 1 : 0.5 }}
              >
                {t('union.dashboard.profile.addBenefitButton')}
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <button
                  onClick={handleSaveBenefits}
                  disabled={savingBenefits || !profileRowsLoaded}
                  style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.86rem', padding: '10px 20px', borderRadius: '8px', cursor: profileRowsLoaded ? 'pointer' : 'default', fontFamily: FONT_BODY, opacity: (savingBenefits || !profileRowsLoaded) ? 0.7 : 1 }}
                >
                  {!profileRowsLoaded ? t('union.dashboard.shared.loadingButton')
                    : savingBenefits ? t('union.dashboard.shared.savingButton')
                    : t('union.dashboard.profile.saveBenefitsButton')}
                </button>
                {benefitsMsg && <span style={{ fontSize: '0.84rem', fontWeight: 600, color: GREEN }}>{benefitsMsg}</span>}
              </div>
            </div>
          </div>
        )}

        {/* ── TEAM: union_staff seats, mirroring the employer Team tab ── */}
        {activeTab === 'Team' && (
          <div>
            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>
              {t('union.dashboard.team.heading')}
            </p>
            <div style={{ maxWidth: 520 }}>
              <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', marginBottom: '18px', lineHeight: 1.5 }}>
                {t('union.dashboard.team.subtitle')}
              </p>

              {/* Current members -- straight from union_staff, so the owner (who never redeemed
                  an invite) shows up here too, not just invited staff. */}
              <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>
                {t('union.dashboard.team.membersHeading')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
                {teamMembers.map(m => {
                  const inactive = m.is_active === false;
                  // The owner can act on anyone but themselves. Self-deactivation is blocked in
                  // the DB trigger regardless; hiding the control keeps them from trying at all.
                  const canToggle = isOwner && m.user_id !== myUserId;
                  return (
                  <div
                    key={m.user_id}
                    data-team-member={m.user_id}
                    data-inactive={inactive ? 'true' : 'false'}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
                      backgroundColor: NAVY_MID, borderRadius: '8px', padding: '10px 14px',
                      opacity: inactive ? 0.55 : 1,
                    }}
                  >
                    <UnionAvatar logoUrl={m.avatar_url} initials={(m.display_name || '?')[0]} size={36} bg={NAVY} textColor={WHITE} />
                    {/* minWidth floor so a deactivated row's three trailing items wrap instead of
                        crushing the name at 390px (same fix as the employer Team tab). */}
                    <div style={{ flex: 1, minWidth: '110px' }}>
                      <div style={{
                        fontSize: '0.85rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        color: inactive ? 'rgba(255,255,255,0.55)' : WHITE,
                        textDecoration: inactive ? 'line-through' : 'none',
                      }}>
                        {m.display_name || t('union.dashboard.team.unnamedMember')}
                        {m.user_id === myUserId && <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}> {t('union.dashboard.team.youSuffix')}</span>}
                      </div>
                    </div>
                    {inactive && (
                      <span style={{
                        fontSize: '0.68rem', fontWeight: 700, color: '#F87171', backgroundColor: 'rgba(248,113,113,0.12)',
                        border: '1px solid rgba(248,113,113,0.3)', borderRadius: '999px', padding: '2px 10px', flexShrink: 0,
                      }}>
                        {t('union.dashboard.team.inactiveBadge')}
                      </span>
                    )}
                    <span style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)', backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '999px', padding: '2px 10px', flexShrink: 0 }}>
                      {m.role === 'owner' ? t('union.dashboard.team.roleOwner') : t('union.dashboard.team.roleStaff')}
                    </span>
                    {canToggle && (
                      <button
                        onClick={() => handleToggleStaffActive(m)}
                        disabled={!!staffActiveSaving[m.user_id]}
                        style={{
                          background: 'transparent', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700,
                          padding: '5px 10px', flexShrink: 0, fontFamily: FONT_BODY,
                          border: `1px solid ${inactive ? 'rgba(29,158,117,0.45)' : 'rgba(255,255,255,0.2)'}`,
                          color: inactive ? GREEN : 'rgba(255,255,255,0.6)',
                          cursor: staffActiveSaving[m.user_id] ? 'default' : 'pointer',
                          opacity: staffActiveSaving[m.user_id] ? 0.5 : 1,
                        }}
                      >
                        {t(inactive ? 'union.dashboard.team.reactivateButton' : 'union.dashboard.team.deactivateButton')}
                      </button>
                    )}
                  </div>
                  );
                })}
                {staffActiveError && (
                  <p role="alert" style={{
                    fontSize: '0.8rem', color: '#F87171', lineHeight: 1.5,
                    backgroundColor: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)',
                    borderRadius: '8px', padding: '10px 12px', margin: 0,
                  }}>
                    {staffActiveError}
                  </p>
                )}
              </div>

              {/* Invite generation writes to union_invites, whose INSERT policy is
                  is_union_owner(union_id) -- hidden for staff so they never see an action that
                  would just fail. */}
              {isOwner ? (
                <>
                  <div style={{ marginBottom: '10px', maxWidth: 320 }}>
                    <label htmlFor="union-invite-email" style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '6px' }}>
                      {t('union.dashboard.team.inviteEmailLabel')}
                    </label>
                    <input
                      id="union-invite-email"
                      type="email"
                      value={inviteEmailInput}
                      onChange={e => setInviteEmailInput(e.target.value)}
                      placeholder={t('union.dashboard.team.inviteEmailPlaceholder')}
                      style={{
                        width: '100%', padding: '9px 12px', fontSize: '0.85rem',
                        border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: WHITE, outline: 'none',
                        backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY, colorScheme: 'dark', boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <button
                    onClick={handleGenerateInvite}
                    disabled={generatingInvite}
                    style={{
                      backgroundColor: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.85rem',
                      padding: '10px 20px', borderRadius: '8px', cursor: generatingInvite ? 'default' : 'pointer',
                      fontFamily: FONT_BODY, opacity: generatingInvite ? 0.7 : 1, marginBottom: '16px',
                    }}
                  >
                    {generatingInvite
                      ? t('union.dashboard.team.generatingButton')
                      : inviteEmailInput.trim()
                        ? t('union.dashboard.team.sendInviteButton')
                        : t('union.dashboard.team.generateButton')}
                  </button>

                  {inviteErrorMsg && (
                    <p style={{ fontSize: '0.82rem', color: '#F87171', marginBottom: '14px' }}>{inviteErrorMsg}</p>
                  )}

                  {inviteEmailStatusMsg && (
                    <p style={{ fontSize: '0.82rem', color: inviteEmailStatusMsg.isError ? '#F87171' : GREEN, marginBottom: '14px' }}>
                      {inviteEmailStatusMsg.text}
                    </p>
                  )}

                  {lastInviteLink && (
                    <div data-invite-link style={{ background: NAVY_MID, borderRadius: '10px', padding: '14px 16px', marginBottom: '24px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: '10px' }}>
                      <span style={{ flex: 1, fontSize: '0.82rem', color: WHITE, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                        {lastInviteLink}
                      </span>
                      <button
                        onClick={handleCopyInviteLink}
                        style={{
                          background: inviteLinkCopied ? GREEN : 'rgba(245,166,35,0.12)', border: `1px solid ${inviteLinkCopied ? GREEN : AMBER}`,
                          color: inviteLinkCopied ? WHITE : AMBER, fontWeight: 700, fontSize: '0.78rem', padding: '7px 14px',
                          borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY, flexShrink: 0, whiteSpace: 'nowrap',
                        }}
                      >
                        {inviteLinkCopied ? t('union.dashboard.team.copiedButton') : t('union.dashboard.team.copyButton')}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <p data-owner-only-invite style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)', marginBottom: '20px' }}>
                  {t('union.dashboard.team.ownerOnlyInviteNote')}
                </p>
              )}

              <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>
                {t('union.dashboard.team.sentInvitesHeading')}
              </p>

              {!invitesLoaded ? (
                <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)' }}>{t('union.dashboard.team.loading')}</p>
              ) : staffInvites.length === 0 ? (
                <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)' }}>{t('union.dashboard.team.emptyNone')}</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {staffInvites.map(inv => {
                    const joinedMember = inv.redeemed_by ? teamMembers.find(m => m.user_id === inv.redeemed_by) : null;
                    return (
                      <div
                        key={inv.id}
                        data-invite-row={inv.id}
                        style={{
                          display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center',
                          justifyContent: 'space-between', gap: isMobile ? '6px' : '10px',
                          backgroundColor: NAVY_MID, borderRadius: '8px', padding: '12px 14px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, maxWidth: '100%' }}>
                          <span style={{ fontSize: '0.68rem', fontWeight: 700, color: inv.redeemed_by ? GREEN : AMBER, backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '999px', padding: '2px 10px', flexShrink: 0 }}>
                            {inv.redeemed_by ? t('union.dashboard.team.statusJoined') : t('union.dashboard.team.statusPending')}
                          </span>
                          {joinedMember ? (
                            <span style={{ fontSize: '0.8rem', color: WHITE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {joinedMember.display_name || t('union.dashboard.team.unnamedMember')}
                            </span>
                          ) : inv.email ? (
                            <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {inv.email}
                            </span>
                          ) : null}
                        </div>
                        <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.38)', flexShrink: 0 }}>
                          {new Date(inv.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="ud-mobile-spacer" />
          </div>
        )}

        {/* ── SETTINGS ── */}
        {activeTab === 'Settings' && (
          <div style={{ maxWidth: '480px' }}>
            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>
              {t('union.dashboard.settings.header')}
            </p>

            {/* My Profile -- this individual's own name/avatar (union_staff row), mirroring the
                employer Settings card. Every seat, owner or staff, can edit their own. */}
            <div data-my-profile style={{ background: NAVY_MID, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: WHITE }}>{t('union.dashboard.settings.myProfileHeading')}</h3>
                {myStaffProfile?.role && (
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)', backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '999px', padding: '2px 10px' }}>
                    {isOwner ? t('union.dashboard.shared.ownerBadge') : t('union.dashboard.shared.staffBadge')}
                  </span>
                )}
              </div>
              <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.42)', marginBottom: '14px', lineHeight: 1.5 }}>
                {t('union.dashboard.settings.myProfileSubtitle')}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                <UnionAvatar logoUrl={myStaffProfile?.avatar_url} initials={(myDisplayName || '?')[0]} size={56} bg={NAVY} textColor={WHITE} />
                <div>
                  <label
                    htmlFor="union-staff-avatar-input"
                    style={{
                      display: 'inline-block', background: 'transparent', border: '1.5px solid rgba(255,255,255,0.22)',
                      color: WHITE, fontFamily: FONT_BODY, fontSize: '0.8rem', fontWeight: 600,
                      padding: '7px 14px', borderRadius: '7px', cursor: avatarUploading ? 'default' : 'pointer',
                      opacity: avatarUploading ? 0.6 : 1,
                    }}
                  >
                    {avatarUploading ? t('union.dashboard.settings.avatarUploading') : t('union.dashboard.settings.avatarChangeButton')}
                  </label>
                  <input id="union-staff-avatar-input" type="file" accept="image/*" onChange={handleAvatarUpload} disabled={avatarUploading} style={{ display: 'none' }} />
                </div>
              </div>
              <div style={fld}>
                <label htmlFor="union-staff-name" style={lbl}>{t('union.dashboard.settings.myNameLabel')}</label>
                <input id="union-staff-name" style={inp} type="text" placeholder={t('union.dashboard.settings.myNamePlaceholder')} value={profileNameForm} onChange={e => setProfileNameForm(e.target.value)} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                <button
                  onClick={handleSaveProfileName}
                  disabled={profileSaving}
                  style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.88rem', padding: '9px 20px', borderRadius: '8px', cursor: profileSaving ? 'default' : 'pointer', fontFamily: FONT_BODY, opacity: profileSaving ? 0.7 : 1 }}
                >
                  {t('union.dashboard.settings.saveNameButton')}
                </button>
                {myProfileMsg && (
                  <span style={{ fontSize: '0.84rem', fontWeight: 600, color: myProfileMsgIsError ? '#F87171' : GREEN }}>
                    {myProfileMsg}
                  </span>
                )}
              </div>
            </div>

            {avatarCropFile && (
              <ImageCropModal
                file={avatarCropFile}
                onCancel={() => setAvatarCropFile(null)}
                onConfirm={handleAvatarCropConfirm}
              />
            )}

            <div style={{ background: NAVY_MID, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: WHITE, marginBottom: '14px' }}>{t('employer.dashboard.settings.changePasswordHeading')}</h3>
              <div style={fld}>
                <label style={lbl}>{t('employer.dashboard.settings.newPasswordLabel')}</label>
                <input style={inp} type="password" placeholder={t('worker.signup.step1.passwordPlaceholder')} value={pwForm.newPw} onChange={e => setPwForm(p => ({ ...p, newPw: e.target.value }))} />
              </div>
              <div style={fld}>
                <label style={lbl}>{t('employer.dashboard.settings.confirmNewPasswordLabel')}</label>
                <input style={inp} type="password" placeholder={t('worker.signup.step1.confirmPasswordPlaceholder')} value={pwForm.confirmPw} onChange={e => setPwForm(p => ({ ...p, confirmPw: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <button
                  onClick={handleChangePassword}
                  style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.88rem', padding: '9px 20px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY }}
                >
                  {t('employer.dashboard.settings.updatePasswordButton')}
                </button>
                {pwMsg && (
                  <span style={{ fontSize: '0.84rem', fontWeight: 600, color: pwMsgIsSuccess ? GREEN : '#F87171' }}>
                    {pwMsg}
                  </span>
                )}
              </div>
            </div>

            <div style={{ background: NAVY_MID, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: WHITE, marginBottom: '14px' }}>{t('employer.dashboard.settings.notificationsHeading')}</h3>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                <div>
                  <p style={{ fontSize: '0.88rem', fontWeight: 600, color: WHITE }}>{t('employer.dashboard.settings.emailNotificationsLabel')}</p>
                  <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.42)', marginTop: '3px', lineHeight: 1.5 }}>
                    {t('union.dashboard.settings.emailNotificationsHint')}
                  </p>
                  {!isOwner && (
                    <p style={{ fontSize: '0.74rem', color: AMBER, marginTop: '4px', lineHeight: 1.5 }}>
                      {t('union.dashboard.settings.ownerOnlyNotificationsNote')}
                    </p>
                  )}
                </div>
                <div
                  role="switch"
                  aria-checked={emailNotifications}
                  aria-disabled={!isOwner}
                  onClick={isOwner ? handleToggleEmailNotifications : undefined}
                  style={{ cursor: isOwner ? 'pointer' : 'not-allowed', opacity: isOwner ? 1 : 0.5, flexShrink: 0, width: '44px', height: '24px', borderRadius: '12px', backgroundColor: emailNotifications ? AMBER : 'rgba(255,255,255,0.15)', position: 'relative', transition: 'background-color 0.2s' }}
                >
                  <div style={{ position: 'absolute', top: '2px', left: emailNotifications ? '22px' : '2px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: WHITE, transition: 'left 0.2s' }} />
                </div>
              </div>
            </div>

            <HelpSettingsCard onClick={support.open} style={{ marginBottom: '16px' }} />

            <div style={{ background: NAVY_MID, borderRadius: '12px', padding: '20px' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: WHITE, marginBottom: '6px' }}>{t('common.signOut')}</h3>
              <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.38)', marginBottom: '14px' }}>{t('worker.settings.signOutNote')}</p>
              <button
                onClick={handleSignOut}
                style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.35)', color: '#F87171', fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 700, padding: '9px 22px', borderRadius: '8px', cursor: 'pointer' }}
              >
                {t('common.signOut')}
              </button>
            </div>

            {/* Delete or deactivate account -- owner-only: delete_own_union_account() rejects anyone who
                isn't the union owner, so staff never see it. */}
            {isOwner && (
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: '28px', paddingTop: '20px' }}>
              <button
                onClick={() => setShowDeleteAccountModal(true)}
                style={{
                  background: 'transparent', border: '1px solid #DC2626', color: '#DC2626',
                  fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 700,
                  padding: '9px 22px', borderRadius: '8px', cursor: 'pointer',
                }}
              >
                {t('employer.dashboard.settings.deleteOrDeactivateAccountButton')}
              </button>
            </div>
            )}
            <div className="ud-mobile-spacer" />
          </div>
        )}

        {showDeleteAccountModal && isOwner && (
          <DeleteAccountModal
            supabase={supabase}
            userId={userId}
            role="union"
            onClose={() => setShowDeleteAccountModal(false)}
          />
        )}

        {cropFile && (
          <ImageCropModal
            file={cropFile}
            onCancel={() => setCropFile(null)}
            onConfirm={handleLogoCropConfirm}
          />
        )}

        {isMobile && (
          <div style={{ padding: '20px 16px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <LegalLinksRow fontSize="0.7rem" />
            <p style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.25)', fontFamily: FONT_BODY }}>{t('common.copyright')}</p>
          </div>
        )}
      </main>

      {/* ── Mobile bottom nav ── */}
      <nav className="ud-bottom-nav" style={{
        display: 'none', position: 'fixed', bottom: 0, left: 0, right: 0,
        height: 'calc(58px + env(safe-area-inset-bottom))', paddingBottom: 'env(safe-area-inset-bottom)', boxSizing: 'border-box',
        backgroundColor: NAVY_MID, borderTop: '1px solid rgba(255,255,255,0.08)',
        zIndex: 200, alignItems: 'center', justifyContent: 'space-around',
      }}>
        {[
          { tab: 'Dashboard',       Icon: IconHome },
          { tab: 'Apprenticeships', Icon: IconBriefcase },
          { tab: 'Exams',           Icon: IconClipboard },
          { tab: 'Candidates',      Icon: IconUsers },
          { tab: 'Messages',        Icon: IconMsg },
          { tab: 'Profile',         Icon: IconBuilding },
          { tab: 'Team',            Icon: IconUserPlus },
        ].map(({ tab, Icon }) => {
          const isActive = activeTab === tab;
          const badgeCount = tab === 'Messages' ? unreadCount : tab === 'Candidates' ? newCandidatesCount : 0;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              aria-current={isActive ? 'page' : undefined}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', padding: '4px 6px', cursor: 'pointer', flex: 1, position: 'relative', background: 'none', border: 'none', margin: 0, font: 'inherit' }}
            >
              <Icon color={isActive ? AMBER : 'rgba(255,255,255,0.38)'} />
              {badgeCount > 0 && (
                <span style={{
                  position: 'absolute', top: 0, left: '50%', marginLeft: '4px', minWidth: '15px', height: '15px', borderRadius: '8px',
                  backgroundColor: AMBER, color: NAVY, fontSize: '8px', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', lineHeight: 1,
                }}>
                  {badgeCount > 9 ? '9+' : badgeCount}
                </span>
              )}
              <span style={{ fontSize: '0.56rem', fontWeight: 600, color: isActive ? AMBER : 'rgba(255,255,255,0.38)' }}>
                {tab === 'Apprenticeships' ? t('union.mobileNav.postings') : tab === 'Exams' ? t('union.mobileNav.exams') : tab === 'Team' ? t('union.mobileNav.team') : tabLabel(tab)}
              </span>
            </button>
          );
        })}
      </nav>

      {/* ── Post/Edit Apprenticeship modal ── */}
      {showApplicantCompare && (
        <UnionApplicantCompare
          applicants={candidates.filter(c => applicantCompare.isSelected(c.id))}
          onRemove={applicantCompare.remove}
          onClear={applicantCompare.clear}
          onClose={() => setShowApplicantCompare(false)}
        />
      )}

      {showApprModal && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setShowApprModal(false); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            backgroundColor: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
          }}
        >
          <div style={{
            backgroundColor: NAVY, borderRadius: '14px',
            border: '1px solid rgba(255,255,255,0.12)',
            padding: '28px 24px', width: '100%', maxWidth: '540px',
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            <h2 style={{ fontFamily: FONT_HEAD, fontSize: '1.9rem', color: WHITE, letterSpacing: '0.04em', marginBottom: '18px' }}>
              {apprModalMode === 'create' ? t('union.dashboard.apprModal.postNewHeading') : t('union.dashboard.apprModal.editHeading')}
            </h2>

            <div style={fld}>
              <label style={lbl}>{t('union.dashboard.apprModal.titleLabel')}</label>
              <input style={inp} placeholder={t('union.dashboard.apprModal.titlePlaceholder')} value={apprForm.title} onChange={e => setApprForm(f => ({ ...f, title: e.target.value }))} />
            </div>

            <div style={fld}>
              <label style={lbl}>{t('union.dashboard.shared.tradeLabel')}</label>
              <select style={sel} value={apprForm.trade} onChange={e => setApprForm(f => ({ ...f, trade: e.target.value }))}>
                <option value="">{t('union.dashboard.shared.tradePlaceholder')}</option>
                {TRADES.map(tr => <option key={tr.value} value={tr.value}>{t(`worker.tradeNames.${tr.key}`)}</option>)}
              </select>
            </div>

            <div style={fld}>
              <label style={lbl}>{t('union.dashboard.apprenticeships.descriptionLabel')}</label>
              <textarea style={txt} value={apprForm.description} onChange={e => setApprForm(f => ({ ...f, description: e.target.value }))} />
            </div>

            <div style={fld}>
              <label style={lbl}>{t('union.dashboard.apprenticeships.requirementsLabel')} <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.35)' }}>{t('union.dashboard.apprModal.requirementsHint')}</span></label>
              <textarea style={txt} placeholder={t('union.dashboard.apprModal.requirementsPlaceholder')} value={apprForm.requirements} onChange={e => setApprForm(f => ({ ...f, requirements: e.target.value }))} />
            </div>

            {/* A structured flag rather than a line in the free-text requirements above, so
                workers see it as its own badge (the way GI Bill eligibility is shown). */}
            <div style={{ ...fld, display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <input
                type="checkbox"
                id="drug-screening-required"
                checked={apprForm.drug_screening_required}
                onChange={e => setApprForm(f => ({ ...f, drug_screening_required: e.target.checked }))}
                style={{ width: '18px', height: '18px', marginTop: '2px', flexShrink: 0, cursor: 'pointer', accentColor: AMBER }}
              />
              <div>
                <label htmlFor="drug-screening-required" style={{ fontSize: '0.88rem', fontWeight: 600, color: WHITE, cursor: 'pointer' }}>
                  {t('union.dashboard.apprModal.drugScreeningLabel')}
                </label>
                <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', marginTop: '3px', lineHeight: 1.5 }}>
                  {t('union.dashboard.apprModal.drugScreeningHint')}
                </p>
              </div>
            </div>

            {apprModalMode === 'create' && duplicateExamLinks.length > 0 && (
              <div data-duplicate-exams style={{ ...fld, backgroundColor: 'rgba(245,166,35,0.07)', border: '1px dashed rgba(245,166,35,0.35)', borderRadius: '8px', padding: '10px 12px' }}>
                <p style={{ fontSize: '0.64rem', fontWeight: 700, color: AMBER, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '4px' }}>
                  {t('union.dashboard.apprModal.duplicateExamsLabel')}
                </p>
                <p style={{ fontSize: '0.84rem', color: WHITE, fontWeight: 600 }}>{duplicateExamLinks.map(l => l.name).join(', ')}</p>
                <p style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.45)', marginTop: '3px' }}>{t('union.dashboard.apprModal.duplicateExamsHint')}</p>
              </div>
            )}

            <div style={g2}>
              <div>
                <label style={lbl}>{t('union.dashboard.apprModal.startDateLabel')}</label>
                <input
                  style={inp} type="date"
                  min={new Date().toISOString().slice(0, 10)}
                  value={apprForm.start_date}
                  onChange={e => setApprForm(f => ({ ...f, start_date: e.target.value }))}
                />
              </div>
              <div>
                <label style={lbl}>{t('union.dashboard.apprModal.durationLabel')}</label>
                <select
                  style={sel}
                  value={apprForm.duration_preset}
                  onChange={e => {
                    const preset = e.target.value;
                    setApprForm(f => ({
                      ...f,
                      duration_preset: preset,
                      duration: preset === 'Other' ? f.duration_custom : preset,
                    }));
                  }}
                >
                  <option value="">{t('union.dashboard.apprModal.durationPlaceholder')}</option>
                  {DURATION_OPTIONS.map(o => <option key={o} value={o}>{t(`union.dashboard.apprModal.durationOptions.${o}`)}</option>)}
                  <option value="Other">{t('union.dashboard.apprModal.otherOption')}</option>
                </select>
                {apprForm.duration_preset === 'Other' && (
                  <input
                    style={{ ...inp, marginTop: '8px' }}
                    placeholder={t('union.dashboard.apprModal.otherPlaceholder')}
                    value={apprForm.duration_custom}
                    onChange={e => setApprForm(f => ({ ...f, duration_custom: e.target.value, duration: e.target.value }))}
                  />
                )}
              </div>
            </div>

            <div style={g2}>
              <div>
                <label style={lbl}>{t('union.dashboard.apprModal.startingRateLabel')}</label>
                <input style={inp} type="number" placeholder="18" value={apprForm.hourly_rate_start} onChange={e => setApprForm(f => ({ ...f, hourly_rate_start: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>{t('union.dashboard.apprModal.journeymanRateLabel')}</label>
                <input style={inp} type="number" placeholder="52" value={apprForm.hourly_rate_journeyman} onChange={e => setApprForm(f => ({ ...f, hourly_rate_journeyman: e.target.value }))} />
              </div>
            </div>

            <div style={g2}>
              <div>
                <label style={lbl}>{t('union.dashboard.shared.cityLabel')}</label>
                <input style={inp} value={apprForm.city} onChange={e => setApprForm(f => ({ ...f, city: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>{t('union.dashboard.shared.stateLabel')}</label>
                <select style={sel} value={apprForm.state} onChange={e => setApprForm(f => ({ ...f, state: e.target.value }))}>
                  <option value="IL">Illinois</option>
                  <option value="IN">Indiana</option>
                  <option value="WI">Wisconsin</option>
                  <option value="MI">Michigan</option>
                  <option value="OH">Ohio</option>
                  <option value="MO">Missouri</option>
                  <option value="IA">Iowa</option>
                  <option value="MN">Minnesota</option>
                </select>
              </div>
            </div>

            <div style={{ ...fld, display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <input
                type="checkbox"
                id="gi-bill-eligible"
                checked={apprForm.gi_bill_eligible}
                onChange={e => setApprForm(f => ({ ...f, gi_bill_eligible: e.target.checked }))}
                style={{ width: '18px', height: '18px', marginTop: '2px', flexShrink: 0, cursor: 'pointer', accentColor: AMBER }}
              />
              <div>
                <label htmlFor="gi-bill-eligible" style={{ fontSize: '0.88rem', fontWeight: 600, color: WHITE, cursor: 'pointer' }}>
                  {t('union.dashboard.apprModal.giBillLabel')}
                </label>
                <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', marginTop: '3px', lineHeight: 1.5 }}>
                  {t('union.dashboard.apprModal.giBillHint')}
                </p>
              </div>
            </div>

            {/* Optional resource link: a video or guide applicants should see before applying. */}
            <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px dashed rgba(255,255,255,0.12)' }}>
              <label htmlFor="appr-resource-url" style={lbl}>{t('union.dashboard.apprModal.resourceLinkLabel')}</label>
              <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', marginTop: '-2px', marginBottom: '8px', lineHeight: 1.45 }}>
                {t('union.dashboard.apprModal.resourceLinkHint')}
              </p>
              <div className="ud-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <input
                  id="appr-resource-url" data-resource-url style={inp} type="url" inputMode="url"
                  placeholder={t('union.dashboard.apprModal.resourceLinkUrlPlaceholder')}
                  value={apprForm.resource_link_url}
                  onChange={e => setApprForm(f => ({ ...f, resource_link_url: e.target.value }))}
                />
                <input
                  data-resource-label style={inp} maxLength={80}
                  aria-label={t('union.dashboard.apprModal.resourceLinkLabel')}
                  placeholder={t('union.dashboard.apprModal.resourceLinkLabelPlaceholder')}
                  value={apprForm.resource_link_label}
                  onChange={e => setApprForm(f => ({ ...f, resource_link_label: e.target.value }))}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button
                onClick={handleSaveApprenticeship}
                disabled={savingAppr}
                style={{ flex: 1, padding: '11px', borderRadius: '8px', border: 'none', backgroundColor: AMBER, color: NAVY, fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 700, cursor: 'pointer', opacity: savingAppr ? 0.7 : 1 }}
              >
                {savingAppr ? t('union.dashboard.shared.savingButton') : apprModalMode === 'create' ? t('union.dashboard.apprModal.postButton') : t('union.dashboard.apprModal.saveChangesButton')}
              </button>
              <button
                onClick={() => setShowApprModal(false)}
                style={{ padding: '11px 18px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'transparent', color: 'rgba(255,255,255,0.7)', fontFamily: FONT_BODY, fontSize: '14px', cursor: 'pointer' }}
              >
                {t('union.dashboard.shared.cancelButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
